import './TiffPdfComponent.scss';
import React, {useState, useEffect, useCallback, useMemo, useRef} from 'react';
import {
  getFontOptions,
  getFontStyleOptions,
  getFontSizeOptions,
  getInsertFieldTypes,
  getPageOptions,
  getPageTypes,
  getPageSizes,
  getRotationOptions,
  getPreserveColorOptions,
  getDocumentSelectionMethods,
  getRedactionTypeOptions,
  DEFAULT_FONT_FAMILY,
  DEFAULT_FONT_STYLE,
  DEFAULT_FONT_SIZE,
  DocumentSelectionMethod,
  PreserveColorOption,
  SlipSheetTreeId,
  ModuleType,
  ComponentType,
  FormMode,
} from '../../CreateNewProductionUtils';
import {
  Box,
  Typography,
  FormControlLabel,
  RadioGroup,
  Radio,
  Divider,
} from '@mui/material';
import {useTranslation, Trans} from 'react-i18next';
import {ArrToggle} from '../../../../common/arr-toggle/ArrToggle';
import ArrButton from '../../../../common/arr-button/ArrButton';
import ArrModal from '../../../../common/arr-modal/ArrModal';
import ArrMultiSelectDropdown from '../../../../common/arr-select-dropdown/ArrMultiSelectDropdown';
import TagsTreePanel from '../../common/tags-tree-panel/TagsTreePanel';
import {ISelectItem} from '../../../../../shared/types';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {
  transformTagTreeData,
  convertTagItemToTreeView,
  convertTreeViewToTagItem,
  mapIdsToTags,
  convertMetadataToTreeItems,
  getPreselectedItems,
  wrapTextWithTag,
  convertPTagsToLineBreaks,
} from '../../CreateNewProductionHelpers';
import {ExtendedTreeItemProps} from '../../../../../shared/interfaces';
import {
  TagItem,
  BrandingData,
  BrandingPositionKey,
  TiffComponentModel,
  FileTypeItem,
  LoadFileItem,
  ProductionComponentsData,
  GetTemplateDetailsRequestParam,
} from '../../../../../api/types/production.type';
import {ReactComponent as InfoIcon} from '../../../../../assets/images/info-icon.svg';
import {ReactComponent as RadioActiveIcon} from '../../../../../assets/images/radio-active-icon.svg';
import {ReactComponent as RadioEnableIcon} from '../../../../../assets/images/radio-enable-icon.svg';
import ArrTooltipWrapper from '../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import ArrSingleSelectDropdown from '../../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrFilterChip from '../../../../common/arr-filter-chip/ArrFilterChip';
import CustomBrandingAccordion from '../../common/custom-branding-accordion/CustomBrandingAccordion';
import CustomNativeDocsAccordion from '../../common/custom-native-docs-accordion/CustomNativeDocsAccordion';
import CustomRedactionTextAccordion from '../../common/custom-redaction-text-accordion/CustomRedactionTextAccordion';
import BrandingLocation from '../../common/branding-location/BrandingLocation';
import TextPropertyToolbar from '../../common/text-property-toolbar/TextPropertyToolbar';
import TagsSection from '../../common/tags-section/TagsSection';
import {Add as AddIcon} from '@mui/icons-material';
import {
  useGetTiffComponent,
  useGetTemplateTiffComponent,
} from '../../../../../hooks/productions/useProductions';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import ArrTreeItemSelector, {
  TabItem,
  SelectedItemsData,
} from '../../../../common/arr-tree-item-selector/ArrTreeItemSelector';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import ArrCheckbox from '../../../../common/arr-checkbox/ArrCheckbox';
import {setIsFieldsChanged} from '../../../../../redux/slice/productionSlice';

// Component Constants
// Redaction constants
const DEFAULT_REDACTION_TEXT = 'REDACTED';
const DEFAULT_REDACTION_ABBREVIATED_TEXT = 'RED';
const DEFAULT_REDACTION_STYLE = 'WB'; // White with black font

// Load file type constants
const DEFAULT_LOAD_FILE_TYPE = 'OPT';

// Special tag ID constants
const DEFAULT_BRANDING_TAG_ID = -1;
const REDACTION_WITHOUT_TAGS_ID = '-2';
const DEFAULT_BRANDING_TAG_LABEL = 'Default';
const DEFAULT_BRANDING_TAG_LIST = ',-1';

// Component type constants (using enum from utils)

// Default values constants
const DEFAULT_ROTATION = '0';
const DEFAULT_BRANDING_LOCATION = 'leftHeaderBranding';

export interface RedactionData {
  id: string;
  redactionText: string;
  redactionLogicId: number;
  selectedRedactionList: string;
  selectedTags: TagItem[];
  abbrivatedText: string;
}

// Reusable type for default branding data structure
export type DefaultBrandingDataStructure = {
  [key in BrandingPositionKey]: {
    hasContent: boolean;
    content: string;
    fontFamily: string;
    fontStyle: string;
    fontSize: string;
  };
};

// Reusable type for custom branding data structure
export type CustomBrandingDataStructure = {
  [key in BrandingPositionKey]: {
    id: string;
    brandingData: BrandingData;
  }[];
};

export interface TiffPdfValues {
  pageOption?: number;
  pageType?: number;
  pageSize?: number;
  rotation?: string;
  pageOrientation?: number;
  skipNativeProduction?: boolean;
  blankPageRemoval?: boolean;
  preserveColor?: boolean;
  brandingLocation?: string;
  fontFamily?: string;
  fontStyle?: string;
  fontSize?: string;
  // Branding data for edit mode
  defaultBrandingData?: DefaultBrandingDataStructure;
  customBrandingData?: CustomBrandingDataStructure;
  // Color JPG options for edit mode
  colorJpgOption?: string;
  selectedDocuments?: ISelectItem[];
  selectedItemsForChips?: {id: string; value: string}[];
  documentSelectionMethod?: string;
  expandedPlusButtons?: Record<string, boolean>;
  // Placeholder settings
  placeholderSettings?: {
    shouldApplyPlaceholderForErroredDocs: boolean;
    enabledforPrivDocs: boolean;
    enabledforNativeDocs: boolean;
    enabledforExceptionDocs: boolean;
    phImageTextPrivDocs: string;
    phImageTextTechIssueDocs: string;
    selectedPrivTags: TagItem[];
    selectedTechIssueTags: TagItem[];
    imagePlaceHolderList: ImagePlaceholderItem[] | null;
  };
  // Redaction settings
  redactionSettings?: {
    isBurnRedactionSelected: boolean;
    isRedactionByAnnotation: boolean;
    redactionType: number;
    redactionStyle: string;
    redactionDataList: RedactionData[];
    specifiedRedactionList?: string;
  };
  redactionGlobalSelectedTags?: TagItem[];
  // Slip sheet settings
  slipSheetSettings?: {
    isSlipSheetEnabled: boolean;
    selectedMetaDataList: string;
    selectedCalculatedList: string;
    selectedTagList: string;
    selectedFolderList: string;
    selectedCommentsList: string;
    selectedRedactionTagsList: string;
    selectedAssignmentList: string;
  };
  // Generate Image Load File setting
  generateImageLoadFile?: boolean;
}

export interface ImagePlaceholderItem {
  id: string;
  placeholderText: string;
  phFileTypes: string;
  phSelectedTags: string;
  selectedPHFileTypes: FileTypeItem[];
  selectedPHTags: TagItem[];
}

interface PlaceholderSettingsState {
  shouldApplyPlaceholderForErroredDocs: boolean;
  enabledforPrivDocs: boolean;
  enabledforNativeDocs: boolean;
  enabledforExceptionDocs: boolean;
  phImageTextPrivDocs: string;
  phImageTextTechIssueDocs: string;
  selectedPrivTags: TagItem[];
  selectedTechIssueTags: TagItem[];
  imagePlaceHolderList: ImagePlaceholderItem[] | null;
}

export interface TiffPdfComponentProps {
  productionId: number;
  isProductionSet: boolean;
  productionComponentsData?: ProductionComponentsData;
  // Consolidated change callback (DatComponent-style)
  onChange?: (values: TiffPdfValues) => void;
  onConfigAvailable?: (getConfigFn: () => TiffComponentModel) => void;
  onRefetchAvailable?: (refetchFn: () => void) => void;
  initialValues?: TiffPdfValues;
  mode?: FormMode;
  isTemplate?: boolean;
  templateId?: number;
}

const TiffPdfComponent: React.FC<TiffPdfComponentProps> = ({
  productionId: productionId,
  isProductionSet,
  productionComponentsData,
  onChange,
  onConfigAvailable,
  onRefetchAvailable,
  mode = FormMode.CREATE,
  isTemplate = false,
  templateId,
  initialValues = {
    pageOption: 0,
    pageType: 0,
    pageSize: 0,
    rotation: DEFAULT_ROTATION,
    pageOrientation: 0,
    skipNativeProduction: false,
    blankPageRemoval: false,
    preserveColor: false,
    brandingLocation: DEFAULT_BRANDING_LOCATION,
    fontFamily: DEFAULT_FONT_FAMILY,
    fontStyle: DEFAULT_FONT_STYLE,
    fontSize: DEFAULT_FONT_SIZE,
    colorJpgOption: PreserveColorOption.ALL_DOCS,
    documentSelectionMethod: DocumentSelectionMethod.FILE_TYPE,
    selectedDocuments: [] as ISelectItem[],
    selectedItemsForChips: [] as {id: string; value: string}[],
    defaultBrandingData: {} as Record<string, unknown>,
    customBrandingData: {} as Record<string, unknown>,
    expandedPlusButtons: {} as Record<string, boolean>,
    placeholderSettings: {
      shouldApplyPlaceholderForErroredDocs: true,
      enabledforPrivDocs: true,
      enabledforNativeDocs: true,
      enabledforExceptionDocs: false,
      phImageTextPrivDocs: '',
      phImageTextTechIssueDocs: '',
      selectedPrivTags: [] as TagItem[],
      selectedTechIssueTags: [] as TagItem[],
      imagePlaceHolderList: [] as ImagePlaceholderItem[],
    },
    redactionSettings: {
      isBurnRedactionSelected: false,
      isRedactionByAnnotation: true,
      redactionType: 0,
      redactionStyle: DEFAULT_REDACTION_STYLE,
      redactionDataList: [] as RedactionData[],
    },
    slipSheetSettings: {
      isSlipSheetEnabled: false,
      selectedMetaDataList: '',
      selectedCalculatedList: '',
      selectedTagList: '',
      selectedFolderList: '',
      selectedCommentsList: '',
      selectedRedactionTagsList: '',
      selectedAssignmentList: '',
    },
    generateImageLoadFile: true,
  },
}) => {
  const {t} = useTranslation();
  const isViewMode = mode === FormMode.VIEW;
  const [componentIsDirty, setComponentIsDirty] = useState<boolean>(false);
  const dispatch = useDispatch();

  const {securityGroupId, productionSetId} = useSelector(
    (state: RootState) => state.production
  );

  // Determine moduleType from isProductionSet using enum
  const moduleType = useMemo(
    () => (isProductionSet ? ModuleType.PRODUCTION : ModuleType.EXPORT),
    [isProductionSet]
  );

  const {
    data: tiffComponentDataFromProduction,
    isLoading: isTiffComponentLoadingFromProduction,
    error: tiffComponentErrorFromProduction,
    refetch: refetchTiffComponentFromProduction,
  } = useGetTiffComponent(
    {
      SecurityGroupId: securityGroupId,
      moduletype: moduleType,
      parentsetid: productionSetId,
      productionId: productionId,
      exportProductionId: 0,
    },
    !!securityGroupId && !!productionSetId && productionId > 0 && !isTemplate
  );

  const {
    data: tiffComponentDataFromTemplate,
    isLoading: isTiffComponentLoadingFromTemplate,
    error: tiffComponentErrorFromTemplate,
    refetch: refetchTiffComponentFromTemplate,
  } = useGetTemplateTiffComponent(
    {
      templateId: templateId!,
      securityGroupId: securityGroupId,
    } as GetTemplateDetailsRequestParam,
    !!securityGroupId && !!templateId && isTemplate
  );

  // Use the appropriate data based on isTemplate flag
  const tiffComponentData = isTemplate
    ? tiffComponentDataFromTemplate
    : tiffComponentDataFromProduction;
  const isTiffComponentLoading = isTemplate
    ? isTiffComponentLoadingFromTemplate
    : isTiffComponentLoadingFromProduction;
  const tiffComponentError = isTemplate
    ? tiffComponentErrorFromTemplate
    : tiffComponentErrorFromProduction;
  const refetchTiffComponent = isTemplate
    ? refetchTiffComponentFromTemplate
    : refetchTiffComponentFromProduction;

  const isEditMode = !!initialValues?.defaultBrandingData;

  const [pageOption, setPageOption] = useState<number>(
    initialValues.pageOption ?? 0
  );
  const [pageType, setPageType] = useState<number>(initialValues.pageType ?? 0);
  const [pageSize, setPageSize] = useState<number>(initialValues.pageSize ?? 0);
  const [rotation, setRotation] = useState<string>(
    initialValues.rotation ?? DEFAULT_ROTATION
  );
  const [pageOrientation, setPageOrientation] = useState<number>(0);
  const [skipNativeProduction, setSkipNativeProduction] = useState<boolean>(
    initialValues.skipNativeProduction ?? false
  );
  const [blankPageRemoval, setBlankPageRemoval] = useState<boolean>(
    initialValues.blankPageRemoval ?? false
  );
  const [preserveColor, setPreserveColor] = useState<boolean>(
    initialValues.preserveColor ?? false
  );
  const [fontFamily, setFontFamily] = useState<string>(
    initialValues.fontFamily ?? DEFAULT_FONT_FAMILY
  );
  const [fontStyle, setFontStyle] = useState<string>(
    initialValues.fontStyle ?? DEFAULT_FONT_STYLE
  );
  const [fontSize, setFontSize] = useState<string>(
    initialValues.fontSize ?? DEFAULT_FONT_SIZE
  );

  const [showBlankPageModal, setShowBlankPageModal] = useState<boolean>(false);
  const [_pendingBlankPageRemoval, setPendingBlankPageRemoval] =
    useState<boolean>(false);
  const [showSelectDocumentsModal, setShowSelectDocumentsModal] =
    useState<boolean>(false);

  const [colorJpgOption, setColorJpgOption] = useState<string>(
    PreserveColorOption.ALL_DOCS
  );
  const [selectedDocuments, setSelectedDocuments] = useState<ISelectItem[]>([]);
  const [documentSelectionMethod, setDocumentSelectionMethod] =
    useState<string>(DocumentSelectionMethod.FILE_TYPE);

  const [selectedItemsForChips, setSelectedItemsForChips] = useState<
    {id: string; value: string}[]
  >([]);

  const [defaultBrandingSelectedTags, setDefaultBrandingSelectedTags] =
    useState<TagItem[]>([]);

  const [expandedPlusButtons, setExpandedPlusButtons] = useState<{
    [key: string]: boolean;
  }>({
    'header-left-center': false,
    'header-center-right': false,
    'footer-left-center': false,
    'footer-center-right': false,
  });

  // Placeholder settings state
  const [placeholderSettings, setPlaceholderSettings] =
    useState<PlaceholderSettingsState>({
      shouldApplyPlaceholderForErroredDocs: true,
      enabledforPrivDocs: true,
      enabledforNativeDocs: true,
      enabledforExceptionDocs: false,
      phImageTextPrivDocs: '',
      phImageTextTechIssueDocs: '',
      selectedPrivTags: [] as TagItem[],
      selectedTechIssueTags: [] as TagItem[],
      imagePlaceHolderList: [] as ImagePlaceholderItem[],
    });
  const defaultNativeImagePlaceholdersRef = useRef<ImagePlaceholderItem[]>([]);

  // Redaction settings state
  const [redactionSettings, setRedactionSettings] = useState({
    isBurnRedactionSelected: false,
    isRedactionByAnnotation: true,
    redactionType: 0,
    redactionStyle: DEFAULT_REDACTION_STYLE,
    redactionDataList: [] as RedactionData[],
    specifiedRedactionList: '',
  });

  // Redaction global tags state (for top-level "Select a Redaction Tag" section)
  const [redactionGlobalSelectedTags, setRedactionGlobalSelectedTags] =
    useState<TagItem[]>([]);

  // State for "Redactions without Redaction Tags" checkbox
  const [isRedactionWithoutTags, setIsRedactionWithoutTags] =
    useState<boolean>(true);

  // Slip sheet settings state
  const [slipSheetSettings, setSlipSheetSettings] = useState({
    isSlipSheetEnabled: false,
    selectedMetaDataList: '',
    selectedCalculatedList: '',
    selectedTagList: '',
    selectedFolderList: '',
    selectedCommentsList: '',
    selectedRedactionTagsList: '',
    selectedAssignmentList: '',
  });

  // Generate Image Load File state
  const [generateImageLoadFile, setGenerateImageLoadFile] = useState(true);

  // Keep specifiedRedactionList in sync with redactionGlobalSelectedTags and isRedactionWithoutTags
  useEffect(() => {
    const tagIds = (redactionGlobalSelectedTags || [])
      .map((tag) => tag.tagId)
      .join(',');
    // Add -2 if checkbox is checked
    const specifiedRedactionList = isRedactionWithoutTags
      ? tagIds
        ? `${tagIds},${REDACTION_WITHOUT_TAGS_ID}`
        : REDACTION_WITHOUT_TAGS_ID
      : tagIds;

    setRedactionSettings((prev) => ({
      ...prev,
      specifiedRedactionList,
    }));
  }, [redactionGlobalSelectedTags, isRedactionWithoutTags]);

  // Load file type state (e.g., OPT, DAT) and options from API
  const [loadFileType, setLoadFileType] = useState<string>(
    DEFAULT_LOAD_FILE_TYPE
  );
  const loadFileDropdownItems = useMemo(() => {
    const list = (tiffComponentData?.loadFileList || []) as LoadFileItem[];
    return list.map((item) => ({
      value: item.loadFileTypeCode,
      label: item.loadFileTypeLabel,
    }));
  }, [tiffComponentData]);

  const selectedLoadFileOption = useMemo(() => {
    if (!loadFileDropdownItems || loadFileDropdownItems.length === 0) {
      return {value: DEFAULT_LOAD_FILE_TYPE, label: DEFAULT_LOAD_FILE_TYPE};
    }
    return (
      loadFileDropdownItems.find((it) => it.value === loadFileType) ||
      loadFileDropdownItems[0]
    );
  }, [loadFileDropdownItems, loadFileType]);

  const [currentBrandingPosition, setCurrentBrandingPosition] =
    useState<BrandingPositionKey>('leftHeaderBranding');
  const [hasBrandingLocationButtonBorder, setHasBrandingLocationButtonBorder] =
    useState<boolean>(false);

  const createDefaultBrandingData = () => ({
    hasContent: false,
    content: '',
    fontFamily: DEFAULT_FONT_FAMILY,
    fontStyle: DEFAULT_FONT_STYLE,
    fontSize: DEFAULT_FONT_SIZE,
  });

  const defaultBrandingDataStructure = createDefaultBrandingData();

  const [defaultBrandingData, setDefaultBrandingData] =
    useState<DefaultBrandingDataStructure>({
      leftHeaderBranding: defaultBrandingDataStructure,
      rightHeaderBranding: defaultBrandingDataStructure,
      centerHeaderBranding: defaultBrandingDataStructure,
      leftCenterHeaderBranding: defaultBrandingDataStructure,
      rightCenterHeaderBranding: defaultBrandingDataStructure,
      headerBranding: defaultBrandingDataStructure,
      leftFooterBranding: defaultBrandingDataStructure,
      rightFooterBranding: defaultBrandingDataStructure,
      centerFooterBranding: defaultBrandingDataStructure,
      leftCenterFooterBranding: defaultBrandingDataStructure,
      rightCenterFooterBranding: defaultBrandingDataStructure,
      footerBranding: defaultBrandingDataStructure,
    });

  const [customBrandingData, setCustomBrandingData] =
    useState<CustomBrandingDataStructure>({
      leftHeaderBranding: [],
      rightHeaderBranding: [],
      centerHeaderBranding: [],
      leftCenterHeaderBranding: [],
      rightCenterHeaderBranding: [],
      headerBranding: [],
      leftFooterBranding: [],
      rightFooterBranding: [],
      centerFooterBranding: [],
      leftCenterFooterBranding: [],
      rightCenterFooterBranding: [],
      footerBranding: [],
    });

  // Derive inputs from productionComponentsData (memoized to avoid identity churn)
  const tagTreeData: TreeViewBaseItem<ExtendedTreeItemProps>[] = useMemo(
    () =>
      productionComponentsData?.tagTree
        ? transformTagTreeData(
            productionComponentsData.tagTree,
            SlipSheetTreeId.TAG_TREE
          )
        : [],
    [productionComponentsData?.tagTree]
  );
  const commonMetaDataList = useMemo(
    () => productionComponentsData?.commonMetaDataList,
    [productionComponentsData?.commonMetaDataList]
  );
  const commonProductionFieldList = useMemo(
    () => productionComponentsData?.commonProductionFieldList,
    [productionComponentsData?.commonProductionFieldList]
  );
  const calculatedFieldList = useMemo(
    () => productionComponentsData?.calculatedFieldList,
    [productionComponentsData?.calculatedFieldList]
  );
  const fileGroupTypeList = useMemo(
    () => productionComponentsData?.fileGroupTypeList || [],
    [productionComponentsData?.fileGroupTypeList]
  );

  // Redaction-specific resources from API
  const redactionTagTreeData: TreeViewBaseItem<ExtendedTreeItemProps>[] =
    useMemo(
      () =>
        productionComponentsData?.redactionTagList
          ? transformTagTreeData(
              productionComponentsData.redactionTagList,
              SlipSheetTreeId.REDACTION_TAG_TREE
            )
          : [],
      [productionComponentsData?.redactionTagList]
    );

  // Slip sheet resources from API
  const folderTreeData: TreeViewBaseItem<ExtendedTreeItemProps>[] = useMemo(
    () =>
      productionComponentsData?.folderList
        ? transformTagTreeData(
            productionComponentsData.folderList,
            SlipSheetTreeId.FOLDER_TREE
          )
        : [],
    [productionComponentsData?.folderList]
  );

  const commentsTreeData: TreeViewBaseItem<ExtendedTreeItemProps>[] = useMemo(
    () =>
      productionComponentsData?.commentsList
        ? transformTagTreeData(
            productionComponentsData.commentsList,
            SlipSheetTreeId.COMMENT_TREE
          )
        : [],
    [productionComponentsData?.commentsList]
  );

  const assignmentsTreeData: TreeViewBaseItem<ExtendedTreeItemProps>[] =
    useMemo(
      () =>
        productionComponentsData?.assignmentsList
          ? transformTagTreeData(
              productionComponentsData.assignmentsList,
              SlipSheetTreeId.ASSIGNMENT_TREE
            )
          : [],
      [productionComponentsData?.assignmentsList]
    );

  const redactionStyleOptions: ISelectItem[] = useMemo(() => {
    const list = productionComponentsData?.redactionStyleList || [];
    return list.map((it) => ({value: it.value, label: it.text}));
  }, [productionComponentsData?.redactionStyleList]);

  // Memoized slip sheet tabs data
  const slipSheetTabs = useMemo<TabItem[]>(() => {
    const metadataTreeData = convertMetadataToTreeItems(
      commonMetaDataList,
      SlipSheetTreeId.METADATA_TREE
    );
    const calculatedTreeData = convertMetadataToTreeItems(
      calculatedFieldList,
      SlipSheetTreeId.CALCULATED_TREE
    );

    // Get preselected items from productionComponentsData (for initial load)
    // Fall back to slipSheetSettings if productionComponentsData is not available
    const preselectedMetaDataList =
      productionComponentsData?.tiffSelectedMetaDataList ||
      slipSheetSettings.selectedMetaDataList ||
      '';
    const preselectedCalculatedList =
      productionComponentsData?.tiffSelectedCalculatedList ||
      slipSheetSettings.selectedCalculatedList ||
      '';
    const preselectedTagList =
      productionComponentsData?.tiffSelectedTagList ||
      slipSheetSettings.selectedTagList ||
      '';
    const preselectedFolderList =
      productionComponentsData?.tiffSelectedFolderList ||
      slipSheetSettings.selectedFolderList ||
      '';
    const preselectedRedactionTagsList =
      productionComponentsData?.tiffSelectedRedactionTagsList ||
      slipSheetSettings.selectedRedactionTagsList ||
      '';
    const preselectedCommentsList =
      productionComponentsData?.tiffSelectedCommentsList ||
      slipSheetSettings.selectedCommentsList ||
      '';
    const preselectedAssignmentList =
      productionComponentsData?.tiffSelectedAssignmentList ||
      slipSheetSettings.selectedAssignmentList ||
      '';

    return [
      {
        tabTitle: t(
          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.slipSheets.tabs.metadata'
        ),
        groups: [
          {
            groupTitle: '',
            prefix: 'Tiff-Metadata: ',
            treeData: metadataTreeData,
            treeId: SlipSheetTreeId.METADATA_TREE,
            postfixFieldName: 'label',
            preSelectedItems: getPreselectedItems(
              preselectedMetaDataList,
              metadataTreeData
            ),
          },
        ],
      },
      {
        tabTitle: t(
          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.slipSheets.tabs.workProduct'
        ),
        groups: [
          {
            groupTitle: t(
              'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.slipSheets.groups.folders'
            ),
            prefix: 'TiffFolders: ',
            treeData: folderTreeData,
            treeId: SlipSheetTreeId.FOLDER_TREE,
            postfixFieldName: 'id',
            preSelectedItems: getPreselectedItems(
              preselectedFolderList,
              folderTreeData
            ),
          },
          {
            groupTitle: t(
              'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.slipSheets.groups.tags'
            ),
            prefix: 'TiffTags: ',
            treeData: tagTreeData,
            treeId: SlipSheetTreeId.TAG_TREE,
            postfixFieldName: 'id',
            preSelectedItems: getPreselectedItems(
              preselectedTagList,
              tagTreeData
            ),
          },
          {
            groupTitle: t(
              'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.slipSheets.groups.redactionTags'
            ),
            prefix: 'TiffRedactionTags: ',
            treeData: redactionTagTreeData,
            treeId: SlipSheetTreeId.REDACTION_TAG_TREE,
            postfixFieldName: 'id',
            preSelectedItems: getPreselectedItems(
              preselectedRedactionTagsList,
              redactionTagTreeData
            ),
          },
          {
            groupTitle: t(
              'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.slipSheets.groups.comments'
            ),
            prefix: 'TiffComments: ',
            treeData: commentsTreeData,
            treeId: SlipSheetTreeId.COMMENT_TREE,
            postfixFieldName: 'id',
            preSelectedItems: getPreselectedItems(
              preselectedCommentsList,
              commentsTreeData
            ),
          },
          {
            groupTitle: t(
              'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.slipSheets.groups.assignments'
            ),
            prefix: 'TiffAssignment: ',
            treeData: assignmentsTreeData,
            treeId: SlipSheetTreeId.ASSIGNMENT_TREE,
            postfixFieldName: 'id',
            preSelectedItems: getPreselectedItems(
              preselectedAssignmentList,
              assignmentsTreeData
            ),
          },
        ],
      },
      {
        tabTitle: t(
          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.slipSheets.tabs.calculated'
        ),
        groups: [
          {
            groupTitle: '',
            prefix: 'Tiff-Calculated: ',
            treeData: calculatedTreeData,
            treeId: SlipSheetTreeId.CALCULATED_TREE,
            postfixFieldName: 'id',
            preSelectedItems: getPreselectedItems(
              preselectedCalculatedList,
              calculatedTreeData
            ),
          },
        ],
      },
    ];
  }, [
    commonMetaDataList,
    calculatedFieldList,
    folderTreeData,
    tagTreeData,
    redactionTagTreeData,
    commentsTreeData,
    assignmentsTreeData,
    slipSheetSettings,
    productionComponentsData,
    t,
  ]);

  const tagsData = tagTreeData;

  const [fontOptions, setFontOptions] = useState<ISelectItem[]>([]);
  const [fontStyleOptions, setFontStyleOptions] = useState<ISelectItem[]>([]);
  const [fontSizeOptions, setFontSizeOptions] = useState<ISelectItem[]>([]);
  const [insertFieldTypes, setInsertFieldTypes] = useState<ISelectItem[]>([]);
  const [metadataFieldOptions, setMetadataFieldOptions] = useState<
    ISelectItem[]
  >([]);
  const [productionFieldOptions, setProductionFieldOptions] = useState<
    ISelectItem[]
  >([]);

  useEffect(() => {
    if (tiffComponentData?.tiffComponentModel) {
      const model = tiffComponentData.tiffComponentModel;
      const settings = model.commonTIFFSettings;

      if (settings.pageType !== undefined) {
        setPageType(settings.pageType);
      }

      if (settings.pageFormat !== undefined) {
        setPageSize(settings.pageFormat);
      }

      if (settings.pageRotatePreference !== undefined) {
        setRotation(settings.pageRotatePreference);
      }

      if (settings.preseveColor !== undefined) {
        setPreserveColor(settings.preseveColor);
      }

      if (settings.shouldRemoveBlankPage !== undefined) {
        setBlankPageRemoval(settings.shouldRemoveBlankPage);
      }

      if (settings.shouldSkipGenerationForNative !== undefined) {
        setSkipNativeProduction(settings.shouldSkipGenerationForNative);
      }

      if (settings.brandingSetting) {
        const brandingSetting = settings.brandingSetting;

        const updatedDefaultBrandingData = {...defaultBrandingData};

        Object.entries(brandingSetting).forEach(([apiKey, brandingDetail]) => {
          if (
            brandingDetail &&
            typeof brandingDetail === 'object' &&
            'defaultData' in brandingDetail
          ) {
            const hasContent = !!brandingDetail.defaultData;
            // Convert <p> tags to line breaks when hydrating from API (for display in text field)
            const content = convertPTagsToLineBreaks(
              brandingDetail.defaultData || ''
            );

            // Extract font properties from the default data if available
            // For now, we'll use the global font settings, but this could be enhanced
            // to store font properties per branding position if the API provides them
            updatedDefaultBrandingData[apiKey as BrandingPositionKey] = {
              hasContent,
              content,
              fontFamily: fontFamily,
              fontStyle: fontStyle,
              fontSize: fontSize,
            };
          }
        });

        setDefaultBrandingData(updatedDefaultBrandingData);

        const updatedCustomBrandingData = {...customBrandingData};

        Object.entries(brandingSetting).forEach(([apiKey, brandingDetail]) => {
          if (
            brandingDetail &&
            typeof brandingDetail === 'object' &&
            'brandingDataList' in brandingDetail &&
            brandingDetail.brandingDataList &&
            Array.isArray(brandingDetail.brandingDataList)
          ) {
            // Separate default branding (tagId: -1) from custom branding
            const defaultBrandingItems = brandingDetail.brandingDataList.filter(
              (item: BrandingData) =>
                item.selectedTags &&
                item.selectedTags.length === 1 &&
                item.selectedTags[0].tagId === DEFAULT_BRANDING_TAG_ID
            );

            const customBrandingItems = brandingDetail.brandingDataList.filter(
              (item: BrandingData) =>
                item.selectedTags &&
                item.selectedTags.length > 0 &&
                !(
                  item.selectedTags.length === 1 &&
                  item.selectedTags[0].tagId === DEFAULT_BRANDING_TAG_ID
                )
            );

            // Handle default branding data - update the default branding data for this position
            if (defaultBrandingItems.length > 0) {
              const defaultItem = defaultBrandingItems[0];
              // Convert <p> tags to line breaks when hydrating from API (for display in text field)
              const cleanBrandingText = convertPTagsToLineBreaks(
                defaultItem.brandingText || ''
              );
              updatedDefaultBrandingData[apiKey as BrandingPositionKey] = {
                hasContent: !!defaultItem.brandingText,
                content: cleanBrandingText,
                fontFamily: defaultItem.brandingFont || DEFAULT_FONT_FAMILY,
                fontStyle: defaultItem.brandingFontStyle || DEFAULT_FONT_STYLE,
                fontSize: String(
                  defaultItem.brandingFontSize || parseInt(DEFAULT_FONT_SIZE)
                ),
              };
            }

            // Map the custom branding data to custom branding items
            const customBrandingItemsMapped = customBrandingItems.map(
              (brandingData: BrandingData, index: number) => ({
                id: `custom-${apiKey}-${index}`,
                brandingData: {
                  ...brandingData,
                  // Convert <p> tags to line breaks when hydrating from API (for display in text field)
                  brandingText: convertPTagsToLineBreaks(
                    brandingData.brandingText || ''
                  ),
                  brandingLogicId: brandingData.brandingLogicId || 0,
                  selectedTagList:
                    brandingData.selectedTagList ||
                    (brandingData.selectedTags &&
                    brandingData.selectedTags.length > 0
                      ? brandingData.selectedTags
                          .map((tag) => tag.tagId)
                          .join(',')
                      : DEFAULT_BRANDING_TAG_LIST),
                  selectedTags: brandingData.selectedTags || [],
                  brandingFont:
                    brandingData.brandingFont || DEFAULT_FONT_FAMILY,
                  brandingFontStyle:
                    brandingData.brandingFontStyle || DEFAULT_FONT_STYLE,
                  brandingFontSize:
                    brandingData.brandingFontSize ||
                    parseInt(DEFAULT_FONT_SIZE),
                },
              })
            );

            updatedCustomBrandingData[apiKey as BrandingPositionKey] =
              customBrandingItemsMapped;
          }
        });

        setCustomBrandingData(updatedCustomBrandingData);

        // Auto-expand plus buttons if expanded positions have data
        const updatedExpandedButtons = {...expandedPlusButtons};

        // Check for left-center and right-center header positions
        const hasLeftCenterHeaderData =
          updatedDefaultBrandingData['leftCenterHeaderBranding']?.hasContent ||
          (updatedCustomBrandingData['leftCenterHeaderBranding']?.length || 0) >
            0;
        const hasRightCenterHeaderData =
          updatedDefaultBrandingData['rightCenterHeaderBranding']?.hasContent ||
          (updatedCustomBrandingData['rightCenterHeaderBranding']?.length ||
            0) > 0;
        const hasFullHeaderData =
          updatedDefaultBrandingData['headerBranding']?.hasContent ||
          (updatedCustomBrandingData['headerBranding']?.length || 0) > 0;

        // Check header expansions
        if (
          hasFullHeaderData &&
          hasLeftCenterHeaderData &&
          hasRightCenterHeaderData
        ) {
          updatedExpandedButtons['header-left-center'] = true;
          updatedExpandedButtons['header-center-right'] = true;
        } else if (
          hasFullHeaderData &&
          !hasLeftCenterHeaderData &&
          !hasRightCenterHeaderData
        ) {
          // If only headerBranding has data, expand both plus buttons to show it
          updatedExpandedButtons['header-left-center'] = true;
          updatedExpandedButtons['header-center-right'] = true;
        } else if (hasLeftCenterHeaderData) {
          updatedExpandedButtons['header-left-center'] = true;
        } else if (hasRightCenterHeaderData) {
          updatedExpandedButtons['header-center-right'] = true;
        }

        // Check for left-center and right-center footer positions
        const hasLeftCenterFooterData =
          updatedDefaultBrandingData['leftCenterFooterBranding']?.hasContent ||
          (updatedCustomBrandingData['leftCenterFooterBranding']?.length || 0) >
            0;
        const hasRightCenterFooterData =
          updatedDefaultBrandingData['rightCenterFooterBranding']?.hasContent ||
          (updatedCustomBrandingData['rightCenterFooterBranding']?.length ||
            0) > 0;
        const hasFullFooterData =
          updatedDefaultBrandingData['footerBranding']?.hasContent ||
          (updatedCustomBrandingData['footerBranding']?.length || 0) > 0;

        // Check footer expansions
        if (
          hasFullFooterData &&
          hasLeftCenterFooterData &&
          hasRightCenterFooterData
        ) {
          updatedExpandedButtons['footer-left-center'] = true;
          updatedExpandedButtons['footer-center-right'] = true;
        } else if (
          hasFullFooterData &&
          !hasLeftCenterFooterData &&
          !hasRightCenterFooterData
        ) {
          // If only footerBranding has data, expand both plus buttons to show it
          updatedExpandedButtons['footer-left-center'] = true;
          updatedExpandedButtons['footer-center-right'] = true;
        } else if (hasLeftCenterFooterData) {
          updatedExpandedButtons['footer-left-center'] = true;
        } else if (hasRightCenterFooterData) {
          updatedExpandedButtons['footer-center-right'] = true;
        }

        setExpandedPlusButtons(updatedExpandedButtons);

        // Auto-select branding position based on priority:
        // 1. headerBranding (if has data)
        // 2. leftCenterHeaderBranding (if has data)
        // 3. leftHeaderBranding (default or if has data)
        let autoSelectedPosition: BrandingPositionKey =
          DEFAULT_BRANDING_LOCATION as BrandingPositionKey;

        const checkHeaderBranding =
          updatedDefaultBrandingData['headerBranding']?.hasContent ||
          (updatedCustomBrandingData['headerBranding']?.length || 0) > 0;
        const checkLeftCenterHeaderBranding =
          updatedDefaultBrandingData['leftCenterHeaderBranding']?.hasContent ||
          (updatedCustomBrandingData['leftCenterHeaderBranding']?.length || 0) >
            0;
        const checkLeftHeaderBranding =
          updatedDefaultBrandingData['leftHeaderBranding']?.hasContent ||
          (updatedCustomBrandingData['leftHeaderBranding']?.length || 0) > 0;

        if (checkHeaderBranding) {
          autoSelectedPosition = 'headerBranding';
        } else if (checkLeftCenterHeaderBranding) {
          autoSelectedPosition = 'leftCenterHeaderBranding';
        } else if (checkLeftHeaderBranding) {
          autoSelectedPosition =
            DEFAULT_BRANDING_LOCATION as BrandingPositionKey;
        }

        setCurrentBrandingPosition(autoSelectedPosition);
      }

      if (settings.preserveColorDetails) {
        const preserveDetails = settings.preserveColorDetails;

        if (preserveDetails.isSpecificDocSelected === 1) {
          setColorJpgOption(PreserveColorOption.SPECIFY_DOCS);

          if (preserveDetails.contextType === 0) {
            setDocumentSelectionMethod(DocumentSelectionMethod.FILE_TYPE);
            if (preserveDetails.selectedIDs) {
              const ids = preserveDetails.selectedIDs
                .split(',')
                .map((id) => id.trim());

              const documents = ids.map((id) => {
                const document = fileGroupTypeList?.find(
                  (item) => item.fileTypeID === parseInt(id)
                );
                return {
                  id: id,
                  name: document?.fileTypeLabel || '',
                };
              });
              setSelectedDocuments(documents);

              const itemsForChips = documents.map((doc) => ({
                id: String(doc.id),
                value: String(doc.name),
              }));
              setSelectedItemsForChips(itemsForChips);
            }
          } else if (preserveDetails.contextType === 1) {
            setDocumentSelectionMethod(DocumentSelectionMethod.TAGS);
            if (
              preserveDetails.selectedIDs &&
              tagTreeData &&
              tagTreeData.length > 0
            ) {
              const ids = preserveDetails.selectedIDs
                .split(',')
                .map((id) => id.trim());

              // Map IDs to labels using tag tree data
              const tags = ids.map((id) => {
                const tagId = parseInt(id);
                // Find the tag in the tag tree data
                const findTagInTree = (
                  nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
                ): TreeViewBaseItem<ExtendedTreeItemProps> | null => {
                  for (const node of nodes) {
                    if (node.id === id) {
                      return node;
                    }
                    if (node.children) {
                      const found = findTagInTree(node.children);
                      if (found) return found;
                    }
                  }
                  return null;
                };

                const tagNode = findTagInTree(tagTreeData);
                return {
                  tagId: tagId,
                  tagLabel: tagNode?.label || '', // Fallback label if not found
                };
              });

              setDefaultBrandingSelectedTags(tags);

              const itemsForChips = tags.map((tag) => ({
                id: String(tag.tagId),
                value: String(tag.tagLabel),
              }));
              setSelectedItemsForChips(itemsForChips);
            } else if (
              preserveDetails.selectedIDs &&
              (!tagTreeData || tagTreeData.length === 0)
            ) {
              // Tag tree data not available yet, set up with fallback labels
              // Handle comma-separated values with spaces (e.g., "1, 2, 3")
              const ids = preserveDetails.selectedIDs
                .split(',')
                .map((id) => id.trim());
              const tags = ids.map((id) => ({
                tagId: parseInt(id),
                tagLabel: '', // Fallback label
              }));
              setDefaultBrandingSelectedTags(tags);

              const itemsForChips = tags.map((tag) => ({
                id: String(tag.tagId),
                value: String(tag.tagLabel),
              }));
              setSelectedItemsForChips(itemsForChips);
            }
          }
        } else {
          setColorJpgOption(PreserveColorOption.ALL_DOCS);
        }
      } else {
        setColorJpgOption(PreserveColorOption.ALL_DOCS);
        setDocumentSelectionMethod(DocumentSelectionMethod.FILE_TYPE);
        setSelectedDocuments([]);
        setDefaultBrandingSelectedTags([]);
        setSelectedItemsForChips([]);
      }

      if (settings.pageOrientation !== undefined) {
        setPageOrientation(settings.pageOrientation);
      }

      if (settings.fileType !== undefined) {
        setPageOption(settings.fileType);
      }
      // Populate generate load file and load file type from API
      setGenerateImageLoadFile(model.generateLoadFile);
      const apiLoadType = model.loadFileType || model.selectedLoadFile || '';
      if (apiLoadType) {
        setLoadFileType(apiLoadType);
      } else if (
        tiffComponentData?.loadFileList &&
        tiffComponentData.loadFileList.length > 0
      ) {
        const list = tiffComponentData.loadFileList as LoadFileItem[];
        setLoadFileType(list[0]?.loadFileTypeCode || DEFAULT_LOAD_FILE_TYPE);
      } else {
        setLoadFileType(DEFAULT_LOAD_FILE_TYPE);
      }

      // Hydrate Placeholder settings from API
      if (settings.placeHolderImageSettings) {
        const ph = settings.placeHolderImageSettings;

        const imagePlaceholders =
          ph.imagePHConfigurations?.imagePlaceHolderList ?? [];
        const resolvedImagePH = imagePlaceholders.map((item, idx) => {
          // Resolve file types across the full file group list
          // Only use API-provided selectedPHFileTypes (may be subset) to mark selected
          const apiSelectedIds = (item.selectedPHFileTypes || []).map(
            (ft: {fileTypeID: number}) => ft.fileTypeID
          );

          const selectedPHFileTypes = (fileGroupTypeList || []).map((ft) => ({
            ...ft,
            isFileTypeSelected:
              apiSelectedIds && apiSelectedIds.length > 0
                ? apiSelectedIds.includes(ft.fileTypeID)
                : false,
          }));
          // Resolve tags using general tag tree
          const selectedPHTags = mapIdsToTags(
            item.phSelectedTags || '',
            tagTreeData || []
          );

          return {
            id: `placeholder-${idx}`,
            // Convert <p> tags to line breaks when hydrating from API (for display in text field)
            placeholderText: convertPTagsToLineBreaks(
              item.placeholderText || ''
            ),
            phFileTypes: item.phFileTypes || '',
            phSelectedTags: item.phSelectedTags || '',
            selectedPHFileTypes,
            selectedPHTags,
          } as ImagePlaceholderItem;
        });
        defaultNativeImagePlaceholdersRef.current = resolvedImagePH.map(
          (item) => ({
            ...item,
            selectedPHFileTypes: [...(item.selectedPHFileTypes || [])],
            selectedPHTags: [...(item.selectedPHTags || [])],
          })
        );

        setPlaceholderSettings({
          shouldApplyPlaceholderForErroredDocs:
            ph.shouldApplyPlaceholderForErroredDocs,
          enabledforPrivDocs: ph.enabledforPrivDocs,
          enabledforNativeDocs: ph.enabledforNativeDocs,
          enabledforExceptionDocs: ph.enabledforExceptionDocs,
          // Convert <p> tags to line breaks when hydrating from API (for display in text field)
          phImageTextPrivDocs: convertPTagsToLineBreaks(
            ph.phImageTextPrivDocs || ''
          ),
          phImageTextTechIssueDocs: convertPTagsToLineBreaks(
            ph.phImageTextTechIssueDocs || ''
          ),
          selectedPrivTags: mapIdsToTags(
            ph.phPrivilegeTagList || '',
            tagTreeData || []
          ),
          selectedTechIssueTags: mapIdsToTags(
            ph.phTechIssueTagList || '',
            tagTreeData || []
          ),
          imagePlaceHolderList: resolvedImagePH,
        });
      } else {
        defaultNativeImagePlaceholdersRef.current = [];
      }

      // Hydrate Redaction settings from API
      if (settings.redactionSettings) {
        const rs = settings.redactionSettings;

        // Check if specifiedRedactionList contains -2 (Redactions without Redaction Tags)
        const specifiedRedactionList = rs.specifiedRedactionList || '';
        const hasRedactionWithoutTags = specifiedRedactionList
          .split(',')
          .map((x) => x.trim())
          .includes(REDACTION_WITHOUT_TAGS_ID);

        // If specifiedRedactionList is empty, default to checked; otherwise use the value from API
        const isEmpty =
          !specifiedRedactionList || specifiedRedactionList.trim() === '';
        setIsRedactionWithoutTags(isEmpty ? true : hasRedactionWithoutTags);

        // Filter out -2 from the list before mapping to tags
        const filteredList = specifiedRedactionList
          .split(',')
          .map((x) => x.trim())
          .filter((id) => id !== REDACTION_WITHOUT_TAGS_ID && id !== '')
          .join(',');

        // Global selected redaction tags list
        setRedactionGlobalSelectedTags(
          mapIdsToTags(filteredList, redactionTagTreeData || [])
        );

        const mappedRedactionData =
          rs.redactionConfigurations?.redactionDataList?.map((item, index) => {
            // Use API tags if available, otherwise resolve from CSV
            const selectedTags =
              item.selectedTags && item.selectedTags.length > 0
                ? item.selectedTags
                : mapIdsToTags(
                    item.selectedRedactionList || '',
                    redactionTagTreeData || []
                  );
            return {
              id: `redaction-${index}`,
              // Convert <p> tags to line breaks when hydrating from API (for display in text field)
              redactionText: convertPTagsToLineBreaks(item.redactionText || ''),
              redactionLogicId: item.redactionLogicId || 0,
              selectedRedactionList: item.selectedRedactionList || '',
              selectedTags,
              abbrivatedText: item.abbrivatedText || '',
            } as RedactionData;
          }) || [];

        setRedactionSettings({
          isBurnRedactionSelected: rs.isBurnRedactionSelected,
          isRedactionByAnnotation: rs.isRedactionByAnnotation,
          redactionType: rs.redactionType,
          redactionStyle: rs.redactionStyle,
          redactionDataList: mappedRedactionData,
          specifiedRedactionList: specifiedRedactionList,
        });
      }

      // Hydrate Slip Sheet settings from API
      if (settings.slipSheetSettings) {
        const ss = settings.slipSheetSettings;

        setSlipSheetSettings({
          isSlipSheetEnabled: ss.isSlipSheetEnabled,
          selectedMetaDataList: ss.selectedMetaDataList || '',
          selectedCalculatedList: ss.selectedCalculatedList || '',
          selectedTagList: ss.selectedTagList || '',
          selectedFolderList: ss.selectedFolderList || '',
          selectedCommentsList: ss.selectedCommentsList || '',
          selectedRedactionTagsList: ss.selectedRedactionTagsList || '',
          selectedAssignmentList: ss.selectedAssignmentList || '',
        });
      }
    }
  }, [
    tiffComponentData,
    tagTreeData,
    fileGroupTypeList,
    fontFamily,
    fontStyle,
    fontSize,
    redactionTagTreeData,
  ]);

  useEffect(() => {
    setFontOptions(getFontOptions());

    setFontStyleOptions(getFontStyleOptions());

    setFontSizeOptions(getFontSizeOptions());

    setInsertFieldTypes(getInsertFieldTypes());

    const metadataOpts =
      commonMetaDataList?.map((field) => ({
        value: field.fieldLabel,
        label: field.fieldLabel,
      })) || [];
    setMetadataFieldOptions(metadataOpts);

    // Production field options - using common production fields
    const productionOpts =
      commonProductionFieldList?.map((field) => ({
        value: field.fieldLabel,
        label: field.fieldLabel,
      })) || [];
    setProductionFieldOptions(productionOpts);

    // Initialize branding data for edit mode
    if (isEditMode && initialValues.defaultBrandingData) {
      setDefaultBrandingData(
        initialValues.defaultBrandingData as DefaultBrandingDataStructure
      );
    }

    if (isEditMode && initialValues.customBrandingData) {
      setCustomBrandingData(
        initialValues.customBrandingData as CustomBrandingDataStructure
      );
    }

    // Initialize color JPG options for edit mode
    if (isEditMode && initialValues.colorJpgOption) {
      setColorJpgOption(initialValues.colorJpgOption);
    }

    if (isEditMode && initialValues.selectedDocuments) {
      setSelectedDocuments(initialValues.selectedDocuments);
    }

    if (isEditMode && initialValues.selectedItemsForChips) {
      setSelectedItemsForChips(initialValues.selectedItemsForChips);
    }

    if (isEditMode && initialValues.documentSelectionMethod) {
      setDocumentSelectionMethod(initialValues.documentSelectionMethod);
    }
  }, [
    commonMetaDataList,
    commonProductionFieldList,
    isEditMode,
    initialValues,
  ]);

  // Helper function to generate branding data list for a specific position
  const generateBrandingDataList = (position: BrandingPositionKey) => {
    const customItems =
      customBrandingData[position]?.map((item) => item.brandingData) || [];

    // Check if there's any default content for this position
    const hasDefaultContent =
      defaultBrandingData[position]?.hasContent || false;

    // Check if headerBranding has content (to determine if footerBranding should have default entry)
    const headerBrandingHasContent =
      defaultBrandingData['headerBranding']?.hasContent || false;

    // Positions that should NOT have default entry when empty
    const positionsWithoutDefaultEntry = ['headerBranding', 'footerBranding'];

    // Create default entry if:
    // 1. There's content for this position, OR
    // 2. Position is not headerBranding/footerBranding, OR
    // 3. Position is footerBranding AND headerBranding has content
    const shouldCreateDefaultEntry =
      hasDefaultContent ||
      !positionsWithoutDefaultEntry.includes(position) ||
      (position === 'footerBranding' && headerBrandingHasContent);

    const defaultEntry = shouldCreateDefaultEntry
      ? {
          // Wrap text with <p></p> tags when saving (empty string if no content, to match API null)
          brandingText: hasDefaultContent
            ? wrapTextWithTag(defaultBrandingData[position]?.content || '', 'p')
            : null,
          brandingLogicId: 0,
          selectedTagList: hasDefaultContent ? DEFAULT_BRANDING_TAG_LIST : '',
          selectedTags: hasDefaultContent
            ? [
                {
                  tagId: DEFAULT_BRANDING_TAG_ID,
                  tagLabel: DEFAULT_BRANDING_TAG_LABEL,
                },
              ]
            : [],
          brandingFont:
            defaultBrandingData[position]?.fontFamily || DEFAULT_FONT_FAMILY,
          brandingFontStyle:
            defaultBrandingData[position]?.fontStyle || DEFAULT_FONT_STYLE,
          brandingFontSize: parseInt(
            defaultBrandingData[position]?.fontSize || DEFAULT_FONT_SIZE
          ),
        }
      : null;

    // Format custom items with selectedTagList
    // Wrap brandingText with <p></p> tags when saving
    const customItemsFormatted = customItems.map((item) => ({
      ...item,
      brandingText: wrapTextWithTag(item.brandingText || '', 'p'),
      selectedTagList: item.selectedTags
        ? item.selectedTags.map((tag) => tag.tagId).join(',')
        : '',
    }));

    // Return default entry + custom items if default exists, otherwise just custom items
    return defaultEntry
      ? [defaultEntry, ...customItemsFormatted]
      : customItemsFormatted;
  };

  // Helper function to get branding setting availability and default data
  const getBrandingSettingInfo = (position: BrandingPositionKey) => {
    const hasDefaultContent =
      defaultBrandingData[position]?.hasContent || false;
    const hasCustomItems = customBrandingData[position]?.length > 0 || false;
    const defaultContent = defaultBrandingData[position]?.content || '';

    // Only return true if there's actual content (default or custom branding)
    const hasContent = hasDefaultContent || hasCustomItems;

    return {
      isBrandingSettingAvailable: hasContent,
      // Wrap text with <p></p> tags when saving
      defaultData: wrapTextWithTag(defaultContent, 'p'),
    };
  };

  // Function to get current TIFF/PDF configuration
  const getCurrentConfig = useCallback((): TiffComponentModel => {
    const nativeImagePlaceholdersForSave =
      placeholderSettings.enabledforNativeDocs
        ? (placeholderSettings.imagePlaceHolderList || []).map((item) => ({
            // Wrap text with <p></p> tags when saving
            placeholderText: wrapTextWithTag(item.placeholderText, 'p'),
            phFileTypes: (item.selectedPHFileTypes || [])
              .filter((ft) => ft.isFileTypeSelected)
              .map((ft) => ft.fileTypeID)
              .join(','),
            phSelectedTags: (item.selectedPHTags || [])
              .map((tag) => tag.tagId)
              .join(','),
            selectedPHFileTypes: item.selectedPHFileTypes,
            selectedPHTags: item.selectedPHTags,
          }))
        : null;

    return {
      generateLoadFile: generateImageLoadFile,
      loadFileType: loadFileType || DEFAULT_LOAD_FILE_TYPE,
      tiffOnlyPreviewPages: false,
      noOfPages: 0,
      populateHOFY: false,
      commonTIFFSettings: {
        ctype: null,
        pageType: pageType,
        pageFormat: pageSize,
        pageOrientation: pageOrientation,
        preseveColor: preserveColor,
        isPortraitForExcel: false,
        pageRotatePreference: rotation,
        shouldRemoveBlankPage: blankPageRemoval,
        shouldSkipGenerationForNative: skipNativeProduction,
        brandingSettingList: [],
        brandingSetting: {
          ctype: '',
          leftHeaderBranding: (() => {
            const info = getBrandingSettingInfo('leftHeaderBranding');
            const brandingDataList =
              generateBrandingDataList('leftHeaderBranding');
            const result = {
              brandingPosition: 'LeftHeaderBranding',
              componentType: ComponentType.TIFF,
              isBrandingSettingAvailable: info.isBrandingSettingAvailable,
              defaultData: info.defaultData,
              brandingDataList: brandingDataList,
            };
            return result;
          })(),
          rightHeaderBranding: (() => {
            const info = getBrandingSettingInfo('rightHeaderBranding');
            return {
              brandingPosition: 'RightHeaderBranding',
              componentType: ComponentType.TIFF,
              isBrandingSettingAvailable: info.isBrandingSettingAvailable,
              defaultData: info.defaultData,
              brandingDataList: generateBrandingDataList('rightHeaderBranding'),
            };
          })(),
          centerHeaderBranding: (() => {
            const info = getBrandingSettingInfo('centerHeaderBranding');
            return {
              brandingPosition: 'CenterHeaderBranding',
              componentType: ComponentType.TIFF,
              isBrandingSettingAvailable: info.isBrandingSettingAvailable,
              defaultData: info.defaultData,
              brandingDataList: generateBrandingDataList(
                'centerHeaderBranding'
              ),
            };
          })(),
          leftCenterHeaderBranding: (() => {
            const info = getBrandingSettingInfo('leftCenterHeaderBranding');
            return {
              brandingPosition: 'LeftCenterHeaderBranding',
              componentType: ComponentType.TIFF,
              isBrandingSettingAvailable: info.isBrandingSettingAvailable,
              defaultData: info.defaultData,
              brandingDataList: generateBrandingDataList(
                'leftCenterHeaderBranding'
              ),
            };
          })(),
          rightCenterHeaderBranding: (() => {
            const info = getBrandingSettingInfo('rightCenterHeaderBranding');
            return {
              brandingPosition: 'RightCenterHeaderBranding',
              componentType: ComponentType.TIFF,
              isBrandingSettingAvailable: info.isBrandingSettingAvailable,
              defaultData: info.defaultData,
              brandingDataList: generateBrandingDataList(
                'rightCenterHeaderBranding'
              ),
            };
          })(),
          headerBranding: (() => {
            const info = getBrandingSettingInfo('headerBranding');
            return {
              brandingPosition: 'HeaderBranding',
              componentType: ComponentType.TIFF,
              isBrandingSettingAvailable: info.isBrandingSettingAvailable,
              defaultData: info.defaultData,
              brandingDataList: generateBrandingDataList('headerBranding'),
            };
          })(),
          leftFooterBranding: (() => {
            const info = getBrandingSettingInfo('leftFooterBranding');
            return {
              brandingPosition: 'LeftFooterBranding',
              componentType: ComponentType.TIFF,
              isBrandingSettingAvailable: info.isBrandingSettingAvailable,
              defaultData: info.defaultData,
              brandingDataList: generateBrandingDataList('leftFooterBranding'),
            };
          })(),
          rightFooterBranding: (() => {
            const info = getBrandingSettingInfo('rightFooterBranding');
            return {
              brandingPosition: 'RightFooterBranding',
              componentType: ComponentType.TIFF,
              isBrandingSettingAvailable: info.isBrandingSettingAvailable,
              defaultData: info.defaultData,
              brandingDataList: generateBrandingDataList('rightFooterBranding'),
            };
          })(),
          centerFooterBranding: (() => {
            const info = getBrandingSettingInfo('centerFooterBranding');
            return {
              brandingPosition: 'CenterFooterBranding',
              componentType: ComponentType.TIFF,
              isBrandingSettingAvailable: info.isBrandingSettingAvailable,
              defaultData: info.defaultData,
              brandingDataList: generateBrandingDataList(
                'centerFooterBranding'
              ),
            };
          })(),
          leftCenterFooterBranding: (() => {
            const info = getBrandingSettingInfo('leftCenterFooterBranding');
            return {
              brandingPosition: 'LeftCenterFooterBranding',
              componentType: ComponentType.TIFF,
              isBrandingSettingAvailable: info.isBrandingSettingAvailable,
              defaultData: info.defaultData,
              brandingDataList: generateBrandingDataList(
                'leftCenterFooterBranding'
              ),
            };
          })(),
          rightCenterFooterBranding: (() => {
            const info = getBrandingSettingInfo('rightCenterFooterBranding');
            return {
              brandingPosition: 'RightCenterFooterBranding',
              componentType: ComponentType.TIFF,
              isBrandingSettingAvailable: info.isBrandingSettingAvailable,
              defaultData: info.defaultData,
              brandingDataList: generateBrandingDataList(
                'rightCenterFooterBranding'
              ),
            };
          })(),
          footerBranding: (() => {
            const info = getBrandingSettingInfo('footerBranding');
            return {
              brandingPosition: 'FooterBranding',
              componentType: ComponentType.TIFF,
              isBrandingSettingAvailable: info.isBrandingSettingAvailable,
              defaultData: info.defaultData,
              brandingDataList: generateBrandingDataList('footerBranding'),
            };
          })(),
        },
        placeHolderImageSettings: {
          ctype: '',
          shouldApplyPlaceholderForErroredDocs:
            placeholderSettings.shouldApplyPlaceholderForErroredDocs,
          enabledforPrivDocs: placeholderSettings.enabledforPrivDocs,
          enabledforNativeDocs: placeholderSettings.enabledforNativeDocs,
          enabledforExceptionDocs: placeholderSettings.enabledforExceptionDocs,
          // Wrap text with <p></p> tags when saving
          phImageTextPrivDocs: wrapTextWithTag(
            placeholderSettings.phImageTextPrivDocs,
            'p'
          ),
          phImageTextTechIssueDocs: wrapTextWithTag(
            placeholderSettings.phImageTextTechIssueDocs,
            'p'
          ),
          selectedPrivTagLabelList: placeholderSettings.selectedPrivTags
            .map((tag) => tag.tagLabel)
            .join(','),
          selectedTechIssueTagLabelList:
            placeholderSettings.selectedTechIssueTags
              .map((tag) => tag.tagLabel)
              .join(','),
          phPrivilegeTagList: placeholderSettings.selectedPrivTags
            .map((tag) => tag.tagId)
            .join(','),
          phTechIssueTagList: placeholderSettings.selectedTechIssueTags
            .map((tag) => tag.tagId)
            .join(','),
          selectedPlaceHolderTagList: '',
          imagePHConfigurations: {
            ctype: '',
            imagePlaceHolderList: nativeImagePlaceholdersForSave,
            componentType: ComponentType.TIFF,
          },
        },
        redactionSettings: {
          ctype: '',
          isBurnRedactionSelected: redactionSettings.isBurnRedactionSelected,
          isRedactionByAnnotation: redactionSettings.isRedactionByAnnotation,
          specifiedRedactionList:
            redactionSettings.specifiedRedactionList ||
            (() => {
              const tagIds = (redactionGlobalSelectedTags || [])
                .map((tag) => tag.tagId)
                .join(',');
              // Add -2 if checkbox is checked
              if (isRedactionWithoutTags) {
                return tagIds
                  ? `${tagIds},${REDACTION_WITHOUT_TAGS_ID}`
                  : REDACTION_WITHOUT_TAGS_ID;
              }
              return tagIds;
            })(),
          redactionType: redactionSettings.redactionType,
          redactionStyle: redactionSettings.redactionStyle,
          redactionConfigurations: {
            ctype: '',
            componentType: ComponentType.TIFF,
            redactionDataList: redactionSettings.redactionDataList.map(
              (item) => ({
                // Wrap text with <p></p> tags when saving
                redactionText: wrapTextWithTag(item.redactionText, 'p'),
                redactionLogicId: item.redactionLogicId,
                selectedRedactionList: item.selectedTags
                  .map((tag) => tag.tagId)
                  .join(','),
                selectedTags: item.selectedTags,
                abbrivatedText: item.abbrivatedText,
              })
            ),
          },
        },
        slipSheetSettings: {
          componentType: ComponentType.TIFF,
          ctype: '',
          metaDataList: [],
          calculatedfieldList: [],
          workProductConfigurations: {
            folderList: slipSheetSettings.selectedFolderList,
            tagsList: slipSheetSettings.selectedTagList,
            redactionList: slipSheetSettings.selectedRedactionTagsList,
            commentList: slipSheetSettings.selectedCommentsList,
            assignmentList: slipSheetSettings.selectedAssignmentList,
          },
          isSlipSheetEnabled: slipSheetSettings.isSlipSheetEnabled,
          selectedMetaDataList: slipSheetSettings.selectedMetaDataList,
          selectedCalculatedList: slipSheetSettings.selectedCalculatedList,
          selectedTagList: slipSheetSettings.selectedTagList,
          selectedFolderList: slipSheetSettings.selectedFolderList,
          selectedCommentsList: slipSheetSettings.selectedCommentsList,
          selectedRedactionTagsList:
            slipSheetSettings.selectedRedactionTagsList,
          selectedAssignmentList: slipSheetSettings.selectedAssignmentList,
        },
        fileType: pageOption,
        tiffSelectedTags: [],
        isSelectAllFileTypes: false,
        preserveColorDetails: {
          isSpecificDocSelected:
            colorJpgOption === PreserveColorOption.SPECIFY_DOCS ? 1 : 0,
          contextType:
            documentSelectionMethod === DocumentSelectionMethod.FILE_TYPE
              ? 0
              : 1,
          selectedIDs: (() => {
            if (
              documentSelectionMethod === DocumentSelectionMethod.FILE_TYPE &&
              selectedDocuments.length > 0
            ) {
              // Save document IDs without extra spaces
              const docIds = selectedDocuments
                .map((doc) => String(doc.id).trim())
                .join(',');
              return docIds;
            } else if (
              documentSelectionMethod === DocumentSelectionMethod.TAGS &&
              defaultBrandingSelectedTags.length > 0
            ) {
              // Save tag IDs without extra spaces
              const tagIds = defaultBrandingSelectedTags
                .map((tag) => String(tag.tagId).trim())
                .join(',');
              return tagIds;
            }
            return '';
          })(),
          selectedLabels: '', // Don't save labels to API, only IDs
        },
      },
      selectedLoadFile: loadFileType || DEFAULT_LOAD_FILE_TYPE,
    };
  }, [
    pageOption,
    pageType,
    pageSize,
    rotation,
    pageOrientation,
    skipNativeProduction,
    blankPageRemoval,
    preserveColor,
    currentBrandingPosition,
    colorJpgOption,
    documentSelectionMethod,
    selectedDocuments,
    selectedItemsForChips,
    defaultBrandingData,
    customBrandingData,
    expandedPlusButtons,
    defaultBrandingSelectedTags,
    placeholderSettings,
    redactionSettings,
    slipSheetSettings,
    loadFileType,
    generateImageLoadFile,
    redactionGlobalSelectedTags,
    isRedactionWithoutTags,
  ]);

  // Pass getCurrentConfig function to parent component
  useEffect(() => {
    if (onConfigAvailable) {
      onConfigAvailable(getCurrentConfig);
    }
  }, [onConfigAvailable, getCurrentConfig]);

  // Pass refetchTiffComponent function to parent component
  useEffect(() => {
    if (onRefetchAvailable) {
      onRefetchAvailable(refetchTiffComponent);
    }
  }, [onRefetchAvailable, refetchTiffComponent]);

  // Notify parent component when state changes
  useEffect(() => {
    if (onChange) {
      const currentValues: TiffPdfValues = {
        pageOption,
        pageType,
        pageSize,
        rotation,
        pageOrientation,
        skipNativeProduction,
        blankPageRemoval,
        preserveColor,
        brandingLocation: currentBrandingPosition,
        fontFamily,
        fontStyle,
        fontSize,
        colorJpgOption,
        documentSelectionMethod,
        selectedDocuments,
        selectedItemsForChips,
        defaultBrandingData,
        customBrandingData,
        expandedPlusButtons,
        placeholderSettings,
        redactionSettings,
        redactionGlobalSelectedTags,
        slipSheetSettings,
        generateImageLoadFile,
      };
      onChange(currentValues);
    }
  }, [
    pageOption,
    pageType,
    pageSize,
    rotation,
    pageOrientation,
    skipNativeProduction,
    blankPageRemoval,
    preserveColor,
    currentBrandingPosition,
    fontFamily,
    fontStyle,
    fontSize,
    colorJpgOption,
    documentSelectionMethod,
    selectedDocuments,
    selectedItemsForChips,
    defaultBrandingData,
    customBrandingData,
    expandedPlusButtons,
    placeholderSettings,
    redactionSettings,
    redactionGlobalSelectedTags,
    isRedactionWithoutTags,
    slipSheetSettings,
    generateImageLoadFile,
    onChange,
  ]);

  // File type options from API data
  const fileTypeOptions: ISelectItem[] =
    fileGroupTypeList?.map((fileType) => ({
      id: String(fileType.fileTypeID),
      name: fileType.fileTypeLabel,
    })) || [];

  const handlePageOptionChange = (value: string) => {
    if (isViewMode) return;
    const numericValue = parseInt(value);
    setPageOption(numericValue);
    setPageType(numericValue);
  };

  const handlePageTypeChange = (value: string) => {
    if (isViewMode) return;
    setPageType(parseInt(value));
  };

  const handlePageSizeChange = (value: string) => {
    if (isViewMode) return;
    setPageSize(parseInt(value));
  };

  const handleRotationChange = (value: string) => {
    if (isViewMode) return;
    setRotation(value);
  };

  const handleToggleChange = (toggleName: string, checked: boolean) => {
    if (isViewMode) return;
    switch (toggleName) {
      case 'skipNativeProduction':
        setSkipNativeProduction(checked);
        break;
      case 'blankPageRemoval':
        if (checked) {
          setPendingBlankPageRemoval(true);
          setShowBlankPageModal(true);
        } else {
          setBlankPageRemoval(checked);
        }
        break;
      case 'preserveColor':
        setPreserveColor(checked);
        break;
    }
  };

  const handleBrandingPositionChange = (position: BrandingPositionKey) => {
    setCurrentBrandingPosition(position);
    setHasBrandingLocationButtonBorder(true);
  };

  const handleDefaultBrandingContentChange = (content: string) => {
    if (isViewMode) return;
    setDefaultBrandingData((prev) => ({
      ...prev,
      [currentBrandingPosition]: {
        ...prev[currentBrandingPosition],
        hasContent: content.trim().length > 0,
        content: content,
      },
    }));
  };

  const handleDefaultBrandingFontChange = (
    fontFamily: string,
    fontStyle: string,
    fontSize: string
  ) => {
    if (isViewMode) return;
    setDefaultBrandingData((prev) => ({
      ...prev,
      [currentBrandingPosition]: {
        ...prev[currentBrandingPosition],
        fontFamily: fontFamily,
        fontStyle: fontStyle,
        fontSize: fontSize,
      },
    }));
  };

  const isResetEnabled = () => {
    if (isViewMode) return false;
    const hasAnyContent = Object.keys(defaultBrandingData).some(
      (position) =>
        defaultBrandingData[position as keyof typeof defaultBrandingData]
          ?.hasContent
    );

    const hasAnyFontChanges = Object.keys(defaultBrandingData).some(
      (position) => {
        const data =
          defaultBrandingData[position as keyof typeof defaultBrandingData];
        return (
          data &&
          (data.fontFamily !== DEFAULT_FONT_FAMILY ||
            data.fontStyle !== DEFAULT_FONT_STYLE ||
            data.fontSize !== DEFAULT_FONT_SIZE)
        );
      }
    );

    const hasAnyCustomBranding = Object.keys(customBrandingData).some(
      (position) =>
        customBrandingData[position as keyof typeof customBrandingData]
          ?.length > 0
    );

    const hasAnyExpansion = Object.values(expandedPlusButtons).some(
      (expanded) => expanded
    );

    return (
      hasAnyContent ||
      hasAnyFontChanges ||
      hasAnyCustomBranding ||
      hasAnyExpansion
    );
  };

  const handleDefaultBrandingReset = () => {
    if (isViewMode) return;
    setCurrentBrandingPosition(
      DEFAULT_BRANDING_LOCATION as BrandingPositionKey
    );

    // Reset global font state
    setFontFamily(DEFAULT_FONT_FAMILY);
    setFontStyle(DEFAULT_FONT_STYLE);
    setFontSize(DEFAULT_FONT_SIZE);

    setDefaultBrandingData({
      leftHeaderBranding: defaultBrandingDataStructure,
      rightHeaderBranding: defaultBrandingDataStructure,
      centerHeaderBranding: defaultBrandingDataStructure,
      leftCenterHeaderBranding: defaultBrandingDataStructure,
      rightCenterHeaderBranding: defaultBrandingDataStructure,
      headerBranding: defaultBrandingDataStructure,
      leftFooterBranding: defaultBrandingDataStructure,
      rightFooterBranding: defaultBrandingDataStructure,
      centerFooterBranding: defaultBrandingDataStructure,
      leftCenterFooterBranding: defaultBrandingDataStructure,
      rightCenterFooterBranding: defaultBrandingDataStructure,
      footerBranding: defaultBrandingDataStructure,
    });

    setExpandedPlusButtons({
      'header-left-center': false,
      'header-center-right': false,
      'footer-left-center': false,
      'footer-center-right': false,
    });

    setCustomBrandingData({
      leftHeaderBranding: [],
      rightHeaderBranding: [],
      centerHeaderBranding: [],
      leftCenterHeaderBranding: [],
      rightCenterHeaderBranding: [],
      headerBranding: [],
      leftFooterBranding: [],
      rightFooterBranding: [],
      centerFooterBranding: [],
      leftCenterFooterBranding: [],
      rightCenterFooterBranding: [],
      footerBranding: [],
    });
  };

  const handleAddCustomBranding = () => {
    if (isViewMode) return;
    setComponentIsDirty(true);
    const newCustomBranding = {
      id: `custom-${Date.now()}`,
      brandingData: {
        brandingText: null,
        brandingLogicId: 0,
        selectedTagList: '',
        selectedTags: [],
        brandingFont: DEFAULT_FONT_FAMILY,
        brandingFontStyle: DEFAULT_FONT_STYLE,
        brandingFontSize: parseInt(DEFAULT_FONT_SIZE),
      },
    };

    setCustomBrandingData((prev) => ({
      ...prev,
      [currentBrandingPosition]: [
        ...(prev[currentBrandingPosition] || []),
        newCustomBranding,
      ],
    }));

    setDefaultBrandingData((prev) => ({
      ...prev,
      [currentBrandingPosition]: {
        ...prev[currentBrandingPosition],
        hasContent: true,
      },
    }));
  };

  const handleDeleteCustomBranding = (customBrandingId: string) => {
    setCustomBrandingData((prev) => {
      const updatedCustomBranding = (
        prev[currentBrandingPosition] || []
      ).filter((item) => item.id !== customBrandingId);

      if (updatedCustomBranding.length === 0) {
        const defaultContent = defaultBrandingData[currentBrandingPosition];
        if (!defaultContent?.content || defaultContent.content.trim() === '') {
          setDefaultBrandingData((prevDefault) => ({
            ...prevDefault,
            [currentBrandingPosition]: {
              ...prevDefault[currentBrandingPosition],
              hasContent: false,
            },
          }));
        }
      }

      return {
        ...prev,
        [currentBrandingPosition]: updatedCustomBranding,
      };
    });
  };

  const handleCustomBrandingDataChange = (id: string, data: BrandingData) => {
    setCustomBrandingData((prev) => ({
      ...prev,
      [currentBrandingPosition]: (prev[currentBrandingPosition] || []).map(
        (item) =>
          item.id === id
            ? {
                ...item,
                brandingData: data,
              }
            : item
      ),
    }));
  };

  const handlePlusButtonClick = (plusButtonKey: string) => {
    if (isViewMode) return;
    const isExpanding = !expandedPlusButtons[plusButtonKey];
    setComponentIsDirty(true);
    if (isExpanding) {
      setDefaultBrandingData((prev) => {
        const newData = {...prev};

        const overlapMap: {[key: string]: BrandingPositionKey[]} = {
          'header-left-center': ['leftHeaderBranding', 'centerHeaderBranding'],
          'header-center-right': [
            'rightHeaderBranding',
            'centerHeaderBranding',
          ],
          'footer-left-center': ['leftFooterBranding', 'centerFooterBranding'],
          'footer-center-right': [
            'rightFooterBranding',
            'centerFooterBranding',
          ],
        };

        const positionsToReset = overlapMap[plusButtonKey] || [];

        if (
          plusButtonKey === 'header-left-center' &&
          expandedPlusButtons['header-center-right']
        ) {
          positionsToReset.push(
            'centerHeaderBranding',
            'rightHeaderBranding',
            'rightCenterHeaderBranding'
          );
        } else if (
          plusButtonKey === 'header-center-right' &&
          expandedPlusButtons['header-left-center']
        ) {
          positionsToReset.push(
            'centerHeaderBranding',
            'leftHeaderBranding',
            'leftCenterHeaderBranding'
          );
        } else if (
          plusButtonKey === 'footer-left-center' &&
          expandedPlusButtons['footer-center-right']
        ) {
          positionsToReset.push(
            'centerFooterBranding',
            'rightFooterBranding',
            'rightCenterFooterBranding'
          );
        } else if (
          plusButtonKey === 'footer-center-right' &&
          expandedPlusButtons['footer-left-center']
        ) {
          positionsToReset.push(
            'centerFooterBranding',
            'leftFooterBranding',
            'leftCenterFooterBranding'
          );
        }

        positionsToReset.forEach((position) => {
          newData[position] = defaultBrandingDataStructure;
        });

        return newData;
      });

      setCustomBrandingData((prev) => {
        const newData = {...prev};

        const overlapMap: {[key: string]: BrandingPositionKey[]} = {
          'header-left-center': ['leftHeaderBranding', 'centerHeaderBranding'],
          'header-center-right': [
            'rightHeaderBranding',
            'centerHeaderBranding',
          ],
          'footer-left-center': ['leftFooterBranding', 'centerFooterBranding'],
          'footer-center-right': [
            'rightFooterBranding',
            'centerFooterBranding',
          ],
        };

        const positionsToReset = overlapMap[plusButtonKey] || [];

        if (
          plusButtonKey === 'header-left-center' &&
          expandedPlusButtons['header-center-right']
        ) {
          positionsToReset.push(
            'centerHeaderBranding',
            'rightHeaderBranding',
            'rightCenterHeaderBranding'
          );
        } else if (
          plusButtonKey === 'header-center-right' &&
          expandedPlusButtons['header-left-center']
        ) {
          positionsToReset.push(
            'centerHeaderBranding',
            'leftHeaderBranding',
            'leftCenterHeaderBranding'
          );
        } else if (
          plusButtonKey === 'footer-left-center' &&
          expandedPlusButtons['footer-center-right']
        ) {
          positionsToReset.push(
            'centerFooterBranding',
            'rightFooterBranding',
            'rightCenterFooterBranding'
          );
        } else if (
          plusButtonKey === 'footer-center-right' &&
          expandedPlusButtons['footer-left-center']
        ) {
          positionsToReset.push(
            'centerFooterBranding',
            'leftFooterBranding',
            'leftCenterFooterBranding'
          );
        }

        positionsToReset.forEach((position) => {
          newData[position] = [];
        });

        return newData;
      });
    }

    setExpandedPlusButtons((prev) => ({
      ...prev,
      [plusButtonKey]: !prev[plusButtonKey],
    }));

    if (isExpanding) {
      const expansionPositionMap: {[key: string]: BrandingPositionKey} = {
        'header-left-center': 'leftCenterHeaderBranding',
        'header-center-right': 'rightCenterHeaderBranding',
        'footer-left-center': 'leftCenterFooterBranding',
        'footer-center-right': 'rightCenterFooterBranding',
      };

      let targetPosition: BrandingPositionKey;

      if (
        plusButtonKey === 'header-left-center' &&
        expandedPlusButtons['header-center-right']
      ) {
        targetPosition = 'headerBranding';
      } else if (
        plusButtonKey === 'header-center-right' &&
        expandedPlusButtons['header-left-center']
      ) {
        targetPosition = 'headerBranding';
      } else if (
        plusButtonKey === 'footer-left-center' &&
        expandedPlusButtons['footer-center-right']
      ) {
        targetPosition = 'footerBranding';
      } else if (
        plusButtonKey === 'footer-center-right' &&
        expandedPlusButtons['footer-left-center']
      ) {
        targetPosition = 'footerBranding';
      } else {
        targetPosition = expansionPositionMap[plusButtonKey];
      }

      if (targetPosition) {
        setDefaultBrandingData((prev) => ({
          ...prev,
          [targetPosition]: {
            ...prev[targetPosition],
            hasContent: true,
          },
        }));

        setCurrentBrandingPosition(targetPosition);
      }
    }
  };

  const getPositionDisplayText = (position: BrandingPositionKey) => {
    const positionMap: {[key in BrandingPositionKey]: string} = {
      leftHeaderBranding: 'Top - Left',
      rightHeaderBranding: 'Top - Right',
      centerHeaderBranding: 'Top - Center',
      leftCenterHeaderBranding: 'Top - Left/Center',
      rightCenterHeaderBranding: 'Top - Right/Center',
      headerBranding: 'Top',
      leftFooterBranding: 'Bottom - Left',
      rightFooterBranding: 'Bottom - Right',
      centerFooterBranding: 'Bottom - Center',
      leftCenterFooterBranding: 'Bottom - Left/Center',
      rightCenterFooterBranding: 'Bottom - Right/Center',
      footerBranding: 'Bottom',
    };
    return positionMap[position] || 'Left';
  };

  const getBrandingPositionButtonVariant = (position: BrandingPositionKey) => {
    // Check API data for isBrandingSettingAvailable
    const apiBrandingSetting =
      tiffComponentData?.tiffComponentModel?.commonTIFFSettings
        ?.brandingSetting;
    const apiPositionData = apiBrandingSetting?.[position];
    const isBrandingSettingAvailable =
      apiPositionData?.isBrandingSettingAvailable || false;

    const positionState = defaultBrandingData[position];
    const customBrandingItems = customBrandingData[position];
    const isSelected = position === currentBrandingPosition;

    const hasContent =
      positionState?.hasContent || customBrandingItems?.length > 0;

    // Use API data to determine success state
    // Show green (success) if API has saved data AND local state has content
    // Don't check for unsaved custom branding - keep green even when adding new items
    if (isBrandingSettingAvailable && hasContent) {
      return 'success';
    } else if (isSelected) {
      return 'primary';
    } else if (hasContent) {
      return 'secondary';
    }
    return 'secondary';
  };

  const getBrandingPositionButtonClassName = (
    position: BrandingPositionKey
  ) => {
    // Check API data for isBrandingSettingAvailable
    const apiBrandingSetting =
      tiffComponentData?.tiffComponentModel?.commonTIFFSettings
        ?.brandingSetting;
    const apiPositionData = apiBrandingSetting?.[position];
    const isBrandingSettingAvailable =
      apiPositionData?.isBrandingSettingAvailable || false;

    const positionState = defaultBrandingData[position];
    const customBrandingItems = customBrandingData[position];
    const isSelected = position === currentBrandingPosition;
    let className = 'branding-location-btn';

    const hasContent =
      positionState?.hasContent || customBrandingItems?.length > 0;

    // Button states based on API data:
    // 1. Saved (API data) - Green background, no border
    // 2. Saved + Selected (API data) - Green background with white border
    // 3. Selected (no API data) - Blue background, no border
    // 4. Has Content (unsaved) - Gray background with white border
    // 5. Default - Gray background, no border

    if (isBrandingSettingAvailable && hasContent) {
      // Saved state - Green background (when API has saved data AND local has content)
      // Don't check for unsaved custom branding - keep green even when adding new items
      className += ' saved';
      if (isSelected && hasBrandingLocationButtonBorder) {
        // Saved + Selected - Green with white border
        className += ' selected';
      }
    } else if (isSelected) {
      // Selected (no API data) - Blue background, no border
      className += ' current-selected';
    } else if (hasContent) {
      // Only show gray with border if there's actual content (not just font changes)
      className += ' has-content-unsaved';
    }
    // Default - Gray background, no border (no additional classes)

    return className;
  };

  const handleModalClose = () => {
    setShowBlankPageModal(false);
    setPendingBlankPageRemoval(false);
  };

  const handleModalContinue = () => {
    setBlankPageRemoval(true);
    setShowBlankPageModal(false);
    setPendingBlankPageRemoval(false);
  };

  const handleColorJpgOptionChange = (option: string) => {
    if (isViewMode) return;
    setColorJpgOption(option);

    if (option === PreserveColorOption.SPECIFY_DOCS) {
      if (
        tiffComponentData?.tiffComponentModel?.commonTIFFSettings
          ?.preserveColorDetails
      ) {
        const preserveDetails =
          tiffComponentData.tiffComponentModel.commonTIFFSettings
            .preserveColorDetails;

        if (preserveDetails.contextType === 0 && preserveDetails.selectedIDs) {
          const ids = preserveDetails.selectedIDs
            .split(',')
            .map((id) => id.trim());
          const documents = ids.map((id) => {
            const document = fileGroupTypeList?.find(
              (item) => item.fileTypeID === parseInt(id)
            );
            return {
              id: id,
              name: document?.fileTypeLabel || '',
            };
          });
          setSelectedDocuments(documents);
          setSelectedItemsForChips(
            documents.map((doc) => ({
              id: String(doc.id),
              value: String(doc.name),
            }))
          );
          setDocumentSelectionMethod(DocumentSelectionMethod.FILE_TYPE);
        } else if (
          preserveDetails.contextType === 1 &&
          preserveDetails.selectedIDs
        ) {
          const ids = preserveDetails.selectedIDs
            .split(',')
            .map((id) => id.trim());

          if (tagTreeData && tagTreeData.length > 0) {
            const tags = ids.map((id) => {
              const tagId = parseInt(id);
              const findTagInTree = (
                nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
              ): TreeViewBaseItem<ExtendedTreeItemProps> | null => {
                for (const node of nodes) {
                  if (node.id === id) {
                    return node;
                  }
                  if (node.children) {
                    const found = findTagInTree(node.children);
                    if (found) return found;
                  }
                }
                return null;
              };
              const tagNode = findTagInTree(tagTreeData);
              return {
                tagId: tagId,
                tagLabel: tagNode?.label || ``,
              };
            });
            setDefaultBrandingSelectedTags(tags);
            setSelectedItemsForChips(
              tags.map((tag) => ({
                id: String(tag.tagId),
                value: String(tag.tagLabel),
              }))
            );
            setDocumentSelectionMethod(DocumentSelectionMethod.TAGS);
          } else {
            // Fallback when tag tree not available
            const tags = ids.map((id) => ({
              tagId: parseInt(id),
              tagLabel: ``,
            }));
            setDefaultBrandingSelectedTags(tags);
            setSelectedItemsForChips(
              tags.map((tag) => ({
                id: String(tag.tagId),
                value: String(tag.tagLabel),
              }))
            );
            setDocumentSelectionMethod(DocumentSelectionMethod.TAGS);
          }
        } else {
          // No API data, set defaults
          setSelectedDocuments([]);
          setDefaultBrandingSelectedTags([]);
          setSelectedItemsForChips([]);
          setDocumentSelectionMethod(DocumentSelectionMethod.FILE_TYPE);
        }
      } else {
        // No API data, set defaults
        setSelectedDocuments([]);
        setDefaultBrandingSelectedTags([]);
        setSelectedItemsForChips([]);
        setDocumentSelectionMethod(DocumentSelectionMethod.FILE_TYPE);
      }

      setShowSelectDocumentsModal(true);
    } else {
      // Switching to all-docs, clear selections
      setSelectedDocuments([]);
      setDefaultBrandingSelectedTags([]);
      setSelectedItemsForChips([]);
    }
  };

  const onClickColorJpgOption = (e: React.MouseEvent<HTMLButtonElement>) => {
    setComponentIsDirty(true);
    const target = e.target as HTMLInputElement;
    if (target.value === PreserveColorOption.SPECIFY_DOCS) {
      setShowSelectDocumentsModal(true);
    }
  };

  const handleSelectDocumentsModalClose = () => {
    setShowSelectDocumentsModal(false);
  };

  const handleSelectDocumentsTagsModalSave = () => {
    const itemsForChips: {id: string; value: string}[] = [];

    if (documentSelectionMethod === DocumentSelectionMethod.FILE_TYPE) {
      selectedDocuments.forEach((doc) => {
        itemsForChips.push({id: String(doc.id), value: String(doc.name)});
      });
    } else if (documentSelectionMethod === DocumentSelectionMethod.TAGS) {
      defaultBrandingSelectedTags.forEach((tag) => {
        itemsForChips.push({
          id: String(tag.tagId),
          value: String(tag.tagLabel),
        });
      });
    }

    setSelectedItemsForChips(itemsForChips);
    setShowSelectDocumentsModal(false);
  };

  const handleDocumentSelectionMethodChange = (method: string) => {
    setDocumentSelectionMethod(method);

    if (method === DocumentSelectionMethod.FILE_TYPE) {
      if (
        tiffComponentData?.tiffComponentModel?.commonTIFFSettings
          ?.preserveColorDetails
      ) {
        const preserveDetails =
          tiffComponentData.tiffComponentModel.commonTIFFSettings
            .preserveColorDetails;
        if (preserveDetails.contextType === 0 && preserveDetails.selectedIDs) {
          // This was originally file-type selection, restore it
          const ids = preserveDetails.selectedIDs
            .split(',')
            .map((id) => id.trim());
          const documents = ids.map((id) => {
            const document = fileGroupTypeList?.find(
              (item) => item.fileTypeID === parseInt(id)
            );
            return {
              id: id,
              name: document?.fileTypeLabel || '',
            };
          });
          setSelectedDocuments(documents);
          setSelectedItemsForChips(
            documents.map((doc) => ({
              id: String(doc.id),
              value: String(doc.name),
            }))
          );
        } else {
          setSelectedDocuments([]);
          setSelectedItemsForChips([]);
        }
      } else {
        setSelectedDocuments([]);
        setSelectedItemsForChips([]);
      }
      setDefaultBrandingSelectedTags([]);
    } else if (method === DocumentSelectionMethod.TAGS) {
      if (
        tiffComponentData?.tiffComponentModel?.commonTIFFSettings
          ?.preserveColorDetails
      ) {
        const preserveDetails =
          tiffComponentData.tiffComponentModel.commonTIFFSettings
            .preserveColorDetails;
        if (preserveDetails.contextType === 1 && preserveDetails.selectedIDs) {
          // This was originally tags selection, restore it
          const ids = preserveDetails.selectedIDs
            .split(',')
            .map((id) => id.trim());

          if (tagTreeData && tagTreeData.length > 0) {
            const tags = ids.map((id) => {
              const tagId = parseInt(id);
              const findTagInTree = (
                nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
              ): TreeViewBaseItem<ExtendedTreeItemProps> | null => {
                for (const node of nodes) {
                  if (node.id === id) {
                    return node;
                  }
                  if (node.children) {
                    const found = findTagInTree(node.children);
                    if (found) return found;
                  }
                }
                return null;
              };
              const tagNode = findTagInTree(tagTreeData);
              return {
                tagId: tagId,
                tagLabel: tagNode?.label || ``,
              };
            });
            setDefaultBrandingSelectedTags(tags);
            setSelectedItemsForChips(
              tags.map((tag) => ({
                id: String(tag.tagId),
                value: String(tag.tagLabel),
              }))
            );
          } else {
            // Fallback when tag tree not available
            const tags = ids.map((id) => ({
              tagId: parseInt(id),
              tagLabel: ``,
            }));
            setDefaultBrandingSelectedTags(tags);
            setSelectedItemsForChips(
              tags.map((tag) => ({
                id: String(tag.tagId),
                value: String(tag.tagLabel),
              }))
            );
          }
        } else {
          setDefaultBrandingSelectedTags([]);
          setSelectedItemsForChips([]);
        }
      } else {
        setDefaultBrandingSelectedTags([]);
        setSelectedItemsForChips([]);
      }
      setSelectedDocuments([]);
    }
  };

  const handleRemoveItemFromChips = (itemId: string) => {
    if (isViewMode) return;
    setSelectedItemsForChips((prev) =>
      prev.filter((item) => item.id !== itemId)
    );

    if (documentSelectionMethod === DocumentSelectionMethod.FILE_TYPE) {
      setSelectedDocuments((prev) =>
        prev.filter((doc) => String(doc.id) !== itemId)
      );
    } else if (documentSelectionMethod === DocumentSelectionMethod.TAGS) {
      setDefaultBrandingSelectedTags((prev) =>
        prev.filter((tag) => String(tag.tagId) !== itemId)
      );
    }
    setComponentIsDirty(true);
  };

  // Placeholder handlers
  const handlePlaceholderToggle = (
    setting: keyof typeof placeholderSettings,
    value: boolean
  ) => {
    if (isViewMode) return;
    setComponentIsDirty(true);
    setPlaceholderSettings((prev) => {
      if (setting !== 'enabledforNativeDocs') {
        return {
          ...prev,
          [setting]: value,
        };
      }

      return {
        ...prev,
        [setting]: value,
        imagePlaceHolderList: value
          ? defaultNativeImagePlaceholdersRef.current.map((item) => ({
              ...item,
              selectedPHFileTypes: [...(item.selectedPHFileTypes || [])],
              selectedPHTags: [...(item.selectedPHTags || [])],
            }))
          : null,
      };
    });
  };

  const handlePlaceholderTextChange = (
    setting: 'phImageTextPrivDocs' | 'phImageTextTechIssueDocs',
    value: string
  ) => {
    if (isViewMode) return;
    setPlaceholderSettings((prev) => ({
      ...prev,
      [setting]: value,
    }));
  };

  const handlePlaceholderTagsChange = (
    setting: 'selectedPrivTags' | 'selectedTechIssueTags',
    tags: TagItem[]
  ) => {
    if (isViewMode) return;
    setComponentIsDirty(true);
    setPlaceholderSettings((prev) => ({
      ...prev,
      [setting]: tags,
    }));
  };

  const handleAddImagePlaceholder = () => {
    if (isViewMode) return;
    setComponentIsDirty(true);
    const newPlaceholder: ImagePlaceholderItem = {
      id: `placeholder-${Date.now()}`,
      placeholderText: '',
      phFileTypes: '',
      phSelectedTags: '',
      selectedPHFileTypes: [],
      selectedPHTags: [],
    };
    setPlaceholderSettings((prev) => ({
      ...prev,
      imagePlaceHolderList: [
        ...(prev.imagePlaceHolderList || []),
        newPlaceholder,
      ],
    }));
  };

  const handleUpdateImagePlaceholder = (
    id: string,
    updates: Partial<ImagePlaceholderItem>
  ) => {
    setPlaceholderSettings((prev) => ({
      ...prev,
      imagePlaceHolderList: (prev.imagePlaceHolderList || []).map((item) =>
        item.id === id ? {...item, ...updates} : item
      ),
    }));
  };

  const handleRemoveImagePlaceholder = (id: string) => {
    setPlaceholderSettings((prev) => ({
      ...prev,
      imagePlaceHolderList: (prev.imagePlaceHolderList || []).filter(
        (item) => item.id !== id
      ),
    }));
  };

  // Redaction handlers
  const handleRedactionToggle = (
    setting: keyof typeof redactionSettings,
    value: boolean
  ) => {
    if (isViewMode) return;
    setComponentIsDirty(true);
    setRedactionSettings((prev) => ({
      ...prev,
      [setting]: value,
    }));
  };

  const handleRedactionTypeChange = (value: number) => {
    if (isViewMode) return;
    setRedactionSettings((prev) => ({
      ...prev,
      redactionType: value,
    }));
  };

  const handleRedactionStyleChange = (value: string) => {
    if (isViewMode) return;
    setRedactionSettings((prev) => ({
      ...prev,
      redactionStyle: value,
    }));
  };

  const handleAddRedactionData = () => {
    if (isViewMode) return;
    setComponentIsDirty(true);
    const newRedactionData: RedactionData = {
      id: `redaction-${Date.now()}`,
      redactionText: DEFAULT_REDACTION_TEXT,
      redactionLogicId: 0,
      selectedRedactionList: '',
      selectedTags: [],
      abbrivatedText: DEFAULT_REDACTION_ABBREVIATED_TEXT,
    };
    setRedactionSettings((prev) => ({
      ...prev,
      redactionDataList: [...prev.redactionDataList, newRedactionData],
    }));
  };

  const handleUpdateRedactionData = (
    id: string,
    updates: Partial<RedactionData>
  ) => {
    setRedactionSettings((prev) => ({
      ...prev,
      redactionDataList: prev.redactionDataList.map((item) =>
        item.id === id ? {...item, ...updates} : item
      ),
    }));
  };

  const handleRemoveRedactionData = (id: string) => {
    setRedactionSettings((prev) => ({
      ...prev,
      redactionDataList: prev.redactionDataList.filter(
        (item) => item.id !== id
      ),
    }));
  };

  // Slip sheet handlers
  const handleSlipSheetToggle = (value: boolean) => {
    if (isViewMode) return;
    setSlipSheetSettings((prev) => ({
      ...prev,
      isSlipSheetEnabled: value,
    }));
  };

  useEffect(() => {
    if (componentIsDirty) {
      dispatch(setIsFieldsChanged({isFieldsChanged: true}));
    }
  }, [componentIsDirty]);

  const handleSlipSheetItemsChange = (data: SelectedItemsData) => {
    if (isViewMode) return;
    // Extract selected IDs from grouped data
    const selectedIds: Record<string, string[]> = {
      metadata: [],
      calculated: [],
      folders: [],
      tags: [],
      redactionTags: [],
      comments: [],
      assignments: [],
    };

    data.groupedByTab?.forEach((tabGroup) => {
      if (tabGroup.tabIndex === 0) {
        // Metadata tab
        tabGroup.groups.forEach((group) => {
          group.items.forEach((item) => {
            selectedIds.metadata.push(item.id);
          });
        });
      } else if (tabGroup.tabIndex === 1) {
        // Work Product tab - identify groups by treeId instead of groupIndex
        tabGroup.groups.forEach((group) => {
          const ids: string[] = [];
          group.items.forEach((item) => {
            ids.push(item.id);
          });

          // Identify group by treeId to avoid issues when some groups have no items
          if (group.treeId === SlipSheetTreeId.FOLDER_TREE) {
            selectedIds.folders = ids;
          } else if (group.treeId === SlipSheetTreeId.TAG_TREE) {
            selectedIds.tags = ids;
          } else if (group.treeId === SlipSheetTreeId.REDACTION_TAG_TREE) {
            selectedIds.redactionTags = ids;
          } else if (group.treeId === SlipSheetTreeId.COMMENT_TREE) {
            selectedIds.comments = ids;
          } else if (group.treeId === SlipSheetTreeId.ASSIGNMENT_TREE) {
            selectedIds.assignments = ids;
          }
        });
      } else if (tabGroup.tabIndex === 2) {
        // Calculated tab
        tabGroup.groups.forEach((group) => {
          group.items.forEach((item) => {
            selectedIds.calculated.push(item.id);
          });
        });
      }
    });

    // Update slip sheet settings with selected IDs
    setSlipSheetSettings((prev) => ({
      ...prev,
      selectedMetaDataList: selectedIds.metadata.join(','),
      selectedCalculatedList: selectedIds.calculated.join(','),
      selectedFolderList: selectedIds.folders.join(','),
      selectedTagList: selectedIds.tags.join(','),
      selectedRedactionTagsList: selectedIds.redactionTags.join(','),
      selectedCommentsList: selectedIds.comments.join(','),
      selectedAssignmentList: selectedIds.assignments.join(','),
    }));
  };

  // Generate Image Load File handler
  const handleGenerateImageLoadFileToggle = (value: boolean) => {
    if (isViewMode) return;
    setComponentIsDirty(true);
    setGenerateImageLoadFile(value);
  };

  const pageOptions: ISelectItem[] = getPageOptions();
  const pageTypes: ISelectItem[] = getPageTypes();
  const pageSizes: ISelectItem[] = getPageSizes();
  const rotationOptions: ISelectItem[] = getRotationOptions();
  const preserveColorOptions: ISelectItem[] = getPreserveColorOptions();
  const documentSelectionMethods: ISelectItem[] = getDocumentSelectionMethods();
  const redactionTypeOptions: ISelectItem[] = getRedactionTypeOptions();
  const nativeImagePlaceholders =
    placeholderSettings.imagePlaceHolderList || [];

  // Show loading state
  if (isTiffComponentLoading || tiffComponentError || !tiffComponentData) {
    return <ArrLoadingSpinner />;
  }

  return (
    <Box
      className="tiff-pdf-component"
      role="form"
      aria-label={t(
        'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.pageOptions.title'
      )}
    >
      <Box className="top-row-sections">
        <Box className="first-half">
          {/* Page Options Section */}
          <Box
            className="page-options-section"
            role="group"
            aria-labelledby="page-options-title"
          >
            <Box className="section-header">
              <Typography
                id="page-options-title"
                className="typo-text subhead4_size section-title"
                data-testid={`tiff-pdf-component-title`}
              >
                {t(
                  'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.pageOptions.title'
                )}
              </Typography>
              <ArrTooltipWrapper
                title={t(
                  'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.tooltips.pageOptions.title'
                )}
                trigger={
                  <InfoIcon fontSize="medium" className="info-icon-sm" />
                }
              >
                <Trans
                  i18nKey="productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.tooltips.pageOptions.content"
                  components={{
                    p: <p data-testid={`tiff-pdf-component-p`} />,
                    strong: (
                      <strong data-testid={`tiff-pdf-component-strong`} />
                    ),
                    br: <br data-testid={`tiff-pdf-component-br`} />,
                    ul: <ul data-testid={`tiff-pdf-component-ul`} />,
                    li: <li data-testid={`tiff-pdf-component-li`} />,
                    b: <b data-testid={`tiff-pdf-component-b`} />,
                  }}
                />
              </ArrTooltipWrapper>
            </Box>

            <RadioGroup
              row
              value={pageOption.toString()}
              onChange={(e) => {
                if (isViewMode) return;
                const selectedObj = pageOptions.find(
                  (opt) => opt.value?.toString() === e.target.value
                );
                if (selectedObj)
                  handlePageOptionChange(String(selectedObj.value) || '0');
              }}
              className="custom-radio-group"
              aria-label={t(
                'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.pageOptions.title'
              )}
            >
              {pageOptions.map(({value, label}, index) => {
                return (
                  <FormControlLabel
                    data-testid="page-options-production-and-export"
                    key={value}
                    value={value}
                    control={
                      <Radio
                        icon={<RadioEnableIcon />}
                        checkedIcon={<RadioActiveIcon />}
                        className={`typo-text body3_size`}
                        slotProps={{
                          input: {
                            'aria-label': String(label),
                            'data-testid': `radio-level-${value}`,
                          } as React.InputHTMLAttributes<HTMLInputElement>,
                        }}
                        disabled={isViewMode}
                      />
                    }
                    className="typo-text body3_size"
                    label={
                      <span
                        className="typo-text body3_size radio-text"
                        data-testid={`tiff-pdf-component-label-${index}`}
                      >
                        {label}
                      </span>
                    }
                    disabled={isViewMode}
                  />
                );
              })}
            </RadioGroup>
          </Box>

          {/* Page Type Section */}
          <Box
            className="page-type-section"
            role="group"
            aria-labelledby="page-type-title"
          >
            <Typography
              id="page-type-title"
              className="typo-text subhead4_size section-title"
              data-testid={`tiff-pdf-component-title-text`}
            >
              {t(
                'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.pageType.title'
              )}
            </Typography>

            <RadioGroup
              row
              value={pageType.toString()}
              onChange={(e) => {
                if (isViewMode) return;
                const selectedObj = pageTypes.find(
                  (opt) => opt.value?.toString() === e.target.value
                );
                if (selectedObj)
                  handlePageTypeChange(String(selectedObj.value) || '1');
                setComponentIsDirty(true);
              }}
              className="custom-radio-group"
              aria-label={t(
                'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.pageType.title'
              )}
            >
              {pageTypes.map(({value, label}, index) => {
                return (
                  <FormControlLabel
                    data-testid="page-types-production-and-export"
                    key={value}
                    value={value}
                    control={
                      <Radio
                        icon={<RadioEnableIcon />}
                        checkedIcon={<RadioActiveIcon />}
                        className="typo-text body3_size"
                        disabled={isViewMode}
                        slotProps={{
                          input: {
                            'aria-label': String(label),
                            'data-testid': `radio-level-${value}`,
                          } as React.InputHTMLAttributes<HTMLInputElement>,
                        }}
                      />
                    }
                    className="typo-text body3_size"
                    label={
                      <span
                        className="typo-text body3_size radio-text"
                        data-testid={`tiff-pdf-component-page-types-${index}`}
                      >
                        {label}
                      </span>
                    }
                  />
                );
              })}
            </RadioGroup>
          </Box>

          {/* Page Size Section */}
          <Box
            className="page-size-section"
            role="group"
            aria-labelledby="page-size-title"
          >
            <Typography
              id="page-size-title"
              className="typo-text subhead4_size section-title"
              data-testid={`tiff-pdf-component-title-btn`}
            >
              {t(
                'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.pageSize.title'
              )}
            </Typography>

            <RadioGroup
              row
              value={pageSize.toString()}
              onChange={(e) => {
                if (isViewMode) return;
                const selectedObj = pageSizes.find(
                  (opt) => opt.value?.toString() === e.target.value
                );
                if (selectedObj)
                  handlePageSizeChange(String(selectedObj.value) || '1');
                setComponentIsDirty(true);
              }}
              className="custom-radio-group"
              aria-label={t(
                'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.pageSize.title'
              )}
            >
              {pageSizes.map(({value, label}, index) => {
                return (
                  <FormControlLabel
                    data-testid="page-size-production-and-export"
                    key={value}
                    value={value}
                    control={
                      <Radio
                        icon={<RadioEnableIcon />}
                        checkedIcon={<RadioActiveIcon />}
                        className="typo-text body3_size"
                        disabled={isViewMode}
                        slotProps={{
                          input: {
                            'aria-label': String(label),
                            'data-testid': `radio-level-${value}`,
                          } as React.InputHTMLAttributes<HTMLInputElement>,
                        }}
                      />
                    }
                    className="typo-text body3_size"
                    label={
                      <span
                        className="typo-text body3_size radio-text"
                        data-testid={`tiff-pdf-component-page-sizes-label-${index}`}
                      >
                        {label}
                      </span>
                    }
                  />
                );
              })}
            </RadioGroup>
          </Box>
        </Box>

        {/* Second Half - Rotation Section */}
        <Box className="second-half">
          <Box className="second-first-half">
            <Box
              className="rotation-section"
              role="group"
              aria-labelledby="rotation-title"
            >
              <Typography
                id="rotation-title"
                className="typo-text subhead4_size section-title"
                data-testid={`tiff-pdf-component-title-button`}
              >
                {t(
                  'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.rotation.title'
                )}
              </Typography>

              <ArrSingleSelectDropdown
                dropdownLabel={t(
                  'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.rotation.dropdownLabel'
                )}
                dropdownItems={rotationOptions}
                dropdownItemKey="value"
                dropdownItemDisplay="label"
                selectedValues={
                  rotationOptions.find((item) => item.value === rotation) ||
                  rotationOptions[0]
                }
                onSelectionChange={(item) => {
                  if (isViewMode) return;
                  handleRotationChange(String(item?.value) || DEFAULT_ROTATION);
                  setComponentIsDirty(true);
                }}
                sizeVariant="medium"
                dataTestId="asd-rotation-dropdown"
                disabled={isViewMode}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <Divider className="section-divider" />
      {/* Toggle Switches Section */}
      <Box
        className="toggle-switches-section"
        role="group"
        aria-label={t(
          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.skipNativeProduction.label'
        )}
      >
        <Box className="toggle-option">
          <FormControlLabel
            data-testid="toggle-option-production-and-export"
            control={
              <ArrToggle
                value={skipNativeProduction}
                onChange={(e) => {
                  if (isViewMode) return;
                  handleToggleChange('skipNativeProduction', e.target.checked);
                  setComponentIsDirty(true);
                }}
                toggleSize="medium"
                disabled={isViewMode}
                dataTestId="skip-native-production-toggle"
              />
            }
            label={
              <Box>
                <Typography
                  className="typo-text subhead5_size"
                  data-testid={`tiff-pdf-component-label`}
                >
                  {t(
                    'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.skipNativeProduction.label'
                  )}
                </Typography>
              </Box>
            }
            labelPlacement="end"
          />
          <ArrTooltipWrapper
            title={t(
              'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.tooltips.skipNativeProduction.title'
            )}
            trigger={<InfoIcon fontSize="medium" className="info-icon-sm" />}
          >
            <Trans
              i18nKey="productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.tooltips.skipNativeProduction.content"
              components={{
                p: <p data-testid={`tiff-pdf-component-p-text`} />,
                strong: (
                  <strong data-testid={`tiff-pdf-component-strong-btn`} />
                ),
                br: <br data-testid={`tiff-pdf-component-br-btn`} />,
              }}
            />
          </ArrTooltipWrapper>
        </Box>

        <Box className="toggle-option">
          <FormControlLabel
            data-testid="toggle-option-blank-page-removal"
            control={
              <ArrToggle
                value={blankPageRemoval}
                onChange={(e) => {
                  if (isViewMode) return;
                  handleToggleChange('blankPageRemoval', e.target.checked);
                  setComponentIsDirty(true);
                }}
                toggleSize="medium"
                disabled={isViewMode}
                dataTestId="blank-page-removal-toggle"
              />
            }
            label={
              <Box>
                <Typography
                  className="typo-text subhead5_size"
                  data-testid={`tiff-pdf-component-label-text`}
                >
                  {t(
                    'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.blankPageRemoval.label'
                  )}
                </Typography>
              </Box>
            }
            labelPlacement="end"
          />
        </Box>

        <Box className="toggle-option">
          <FormControlLabel
            control={
              <ArrToggle
                value={preserveColor}
                onChange={(e) => {
                  if (isViewMode) return;
                  handleToggleChange('preserveColor', e.target.checked);
                  setComponentIsDirty(true);
                }}
                toggleSize="medium"
                disabled={isViewMode}
                dataTestId="preserve-color-toggle"
              />
            }
            label={
              <Box>
                <Typography
                  className="typo-text subhead5_size"
                  data-testid={`tiff-pdf-component-label-btn`}
                >
                  {t(
                    'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.preserveColor.label'
                  )}
                </Typography>
              </Box>
            }
            labelPlacement="end"
          />
          <ArrTooltipWrapper
            title={t(
              'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.tooltips.preserveColor.title'
            )}
            trigger={<InfoIcon fontSize="medium" className="info-icon-sm" />}
          >
            <Trans
              i18nKey="productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.tooltips.preserveColor.content"
              components={{
                p: <p data-testid={`tiff-pdf-component-p-btn`} />,
                strong: (
                  <strong data-testid={`tiff-pdf-component-strong-button`} />
                ),
                br: <br data-testid={`tiff-pdf-component-br-button`} />,
                li: <li data-testid={`tiff-pdf-component-li-btn`} />,
              }}
            />
          </ArrTooltipWrapper>
        </Box>

        {/* Color JPG Options - Only show when Preserve Color is enabled */}
        {preserveColor && pageOption === 0 && pageType === 0 && (
          <Box className="color-jpg-options">
            <RadioGroup
              row
              value={colorJpgOption}
              onChange={(e) => {
                if (isViewMode) return;
                const selectedObj = preserveColorOptions.find(
                  (opt) => opt.value === e.target.value
                );
                if (selectedObj) {
                  const value =
                    String(selectedObj.value) || PreserveColorOption.ALL_DOCS;
                  handleColorJpgOptionChange(value);
                }
                setComponentIsDirty(true);
              }}
              className="custom-radio-group"
              aria-label={t(
                'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.preserveColor.label'
              )}
            >
              {preserveColorOptions.map(({value, label}, index) => {
                return (
                  <FormControlLabel
                    key={value}
                    value={value}
                    control={
                      <Radio
                        icon={<RadioEnableIcon />}
                        checkedIcon={<RadioActiveIcon />}
                        className="typo-text body3_size"
                        onClick={onClickColorJpgOption}
                        disabled={isViewMode}
                        slotProps={{
                          input: {
                            'aria-label': String(label),
                            'data-testid': `preserve-color-radio-${value}`,
                          } as React.InputHTMLAttributes<HTMLInputElement>,
                        }}
                      />
                    }
                    className="typo-text body3_size"
                    label={
                      <span
                        className="typo-text body3_size radio-text"
                        data-testid={`tiff-pdf-component-color-option-pres-${index}`}
                      >
                        {label}
                      </span>
                    }
                  />
                );
              })}
            </RadioGroup>

            {/* Selected Items Display - Only show when "Specify Docs" is selected and items are available */}
            {colorJpgOption === PreserveColorOption.SPECIFY_DOCS &&
              selectedItemsForChips.length > 0 && (
                <Box className="selected-items-chips">
                  {selectedItemsForChips.map((item, index) => (
                    <ArrFilterChip
                      key={item.id}
                      filterName=""
                      selections={[item]}
                      variant="v1"
                      isResponsive={false}
                      classNames="arr-filter-chip-v2-radius"
                      onRemove={
                        isViewMode
                          ? undefined
                          : () => handleRemoveItemFromChips(item.id)
                      }
                      disabled={isViewMode}
                      dataTestId={`selected-color-option-${index}`}
                    />
                  ))}
                </Box>
              )}
          </Box>
        )}
      </Box>
      {/* Branding Section */}
      <Box
        className="branding-section"
        role="region"
        aria-labelledby="branding-section-title"
      >
        <Box className="main-section-header">
          <Typography
            id="branding-section-title"
            className="typo-text subhead3_size section-title"
            data-testid={`tiff-pdf-component-title-icon`}
          >
            {t(
              'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.branding.title'
            )}
          </Typography>
          <ArrTooltipWrapper
            title={t(
              'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.tooltips.branding.title'
            )}
            trigger={<InfoIcon fontSize="medium" className="info-icon" />}
          >
            <Trans
              i18nKey="productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.tooltips.branding.content"
              components={{
                p: <p data-testid={`tiff-pdf-component-p-button`} />,
                strong: (
                  <strong data-testid={`tiff-pdf-component-strong-icon`} />
                ),
                br: <br data-testid={`tiff-pdf-component-br-icon`} />,
              }}
            />
          </ArrTooltipWrapper>
        </Box>

        {/* Branding Content - Two Column Layout */}
        <Box className="branding-content">
          {/* Left Column - Default Branding */}
          <Box className="branding-left-column">
            {/* Branding Location */}
            <BrandingLocation
              currentBrandingPosition={currentBrandingPosition}
              expandedPlusButtons={expandedPlusButtons}
              onBrandingPositionChange={handleBrandingPositionChange}
              onPlusButtonClick={handlePlusButtonClick}
              onReset={handleDefaultBrandingReset}
              isResetEnabled={isResetEnabled()}
              getBrandingPositionButtonVariant={
                getBrandingPositionButtonVariant
              }
              getBrandingPositionButtonClassName={
                getBrandingPositionButtonClassName
              }
              getPositionDisplayText={getPositionDisplayText}
            />

            {/* Default Branding */}
            <Box className="default-branding-section">
              <Typography
                className="typo-text subhead4_size branding-section-title"
                data-testid={`tiff-pdf-component-title-link`}
              >
                {t(
                  'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.branding.default.title'
                )}{' '}
                <Typography
                  component="span"
                  className="typo-text subhead4_size branding-section-position-title"
                  data-testid={`tiff-pdf-component-`}
                >
                  ({getPositionDisplayText(currentBrandingPosition)})
                </Typography>
              </Typography>

              <Box className="branding-controls">
                {/* Text Property Toolbar */}
                <TextPropertyToolbar
                  dataTestId={'branding-controls'}
                  fontFamily={
                    defaultBrandingData[currentBrandingPosition]?.fontFamily ||
                    DEFAULT_FONT_FAMILY
                  }
                  fontStyle={
                    defaultBrandingData[currentBrandingPosition]?.fontStyle ||
                    DEFAULT_FONT_STYLE
                  }
                  fontSize={
                    defaultBrandingData[currentBrandingPosition]?.fontSize ||
                    DEFAULT_FONT_SIZE
                  }
                  toolbarText={
                    defaultBrandingData[currentBrandingPosition]?.content || ''
                  }
                  fontOptions={fontOptions}
                  fontStyleOptions={fontStyleOptions}
                  fontSizeOptions={fontSizeOptions}
                  insertFieldTypes={insertFieldTypes}
                  metadataFieldOptions={metadataFieldOptions}
                  productionFieldOptions={productionFieldOptions}
                  onFontFamilyChange={(value) => {
                    handleDefaultBrandingFontChange(
                      value,
                      defaultBrandingData[currentBrandingPosition]?.fontStyle ||
                        DEFAULT_FONT_STYLE,
                      defaultBrandingData[currentBrandingPosition]?.fontSize ||
                        DEFAULT_FONT_SIZE
                    );
                  }}
                  onFontStyleChange={(value) => {
                    handleDefaultBrandingFontChange(
                      defaultBrandingData[currentBrandingPosition]
                        ?.fontFamily || DEFAULT_FONT_FAMILY,
                      value,
                      defaultBrandingData[currentBrandingPosition]?.fontSize ||
                        DEFAULT_FONT_SIZE
                    );
                  }}
                  onFontSizeChange={(value) => {
                    handleDefaultBrandingFontChange(
                      defaultBrandingData[currentBrandingPosition]
                        ?.fontFamily || DEFAULT_FONT_FAMILY,
                      defaultBrandingData[currentBrandingPosition]?.fontStyle ||
                        DEFAULT_FONT_STYLE,
                      value
                    );
                  }}
                  onToolbarTextChange={(content) => {
                    handleDefaultBrandingContentChange(content);
                  }}
                  onInsertField={(field) => {
                    handleDefaultBrandingContentChange(
                      (defaultBrandingData[currentBrandingPosition]?.content ||
                        '') + `$${field}$`
                    );
                  }}
                  placeholder={t(
                    'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.branding.default.description'
                  )}
                  initialToolbarData={
                    defaultBrandingData[currentBrandingPosition] ||
                    defaultBrandingDataStructure
                  }
                  disabled={isViewMode}
                  textAreaTestId="default-branding-placeholder"
                />
              </Box>
            </Box>
          </Box>

          {/* Right Column - Custom Branding */}
          {(!isViewMode ||
            customBrandingData[currentBrandingPosition]?.length > 0) && (
            <Box className="branding-right-column">
              {/* Custom Branding */}
              <Box className="custom-branding-section">
                <Box className="custom-section-header">
                  <Typography
                    className="typo-text subhead4_size branding-section-title"
                    data-testid={`tiff-pdf-component-title-input`}
                  >
                    {t(
                      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.branding.custom.title'
                    )}
                  </Typography>
                  {customBrandingData[currentBrandingPosition]?.length > 0 ? (
                    <Typography
                      component="span"
                      className="typo-text subhead4_size branding-section-position-title"
                      data-testid={`tiff-pdf-component--text`}
                    >
                      ({getPositionDisplayText(currentBrandingPosition)})
                    </Typography>
                  ) : (
                    <ArrTooltipWrapper
                      title={t(
                        'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.tooltips.customBranding.title'
                      )}
                      trigger={
                        <InfoIcon fontSize="medium" className="info-icon-sm" />
                      }
                    >
                      <Trans
                        i18nKey="productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.tooltips.customBranding.content"
                        components={{
                          p: <p data-testid={`tiff-pdf-component-p-icon`} />,
                          strong: (
                            <strong
                              data-testid={`tiff-pdf-component-strong-link`}
                            />
                          ),
                          br: <br data-testid={`tiff-pdf-component-br-link`} />,
                        }}
                      />
                    </ArrTooltipWrapper>
                  )}
                </Box>

                {/* Custom Branding using CustomBrandingAccordion component */}
                {customBrandingData[currentBrandingPosition]?.map(
                  (item, index) => {
                    // Collect all tags from other items (excluding current item)
                    const disabledTags =
                      customBrandingData[currentBrandingPosition]
                        ?.filter((otherItem) => otherItem.id !== item.id)
                        .flatMap(
                          (otherItem) =>
                            otherItem.brandingData?.selectedTags || []
                        ) || [];

                    return (
                      <CustomBrandingAccordion
                        key={item.id}
                        id={item.id}
                        index={index}
                        brandingData={{...item.brandingData, disabledTags}}
                        onDelete={handleDeleteCustomBranding}
                        onBrandingDataChange={handleCustomBrandingDataChange}
                        fontOptions={fontOptions}
                        fontStyleOptions={fontStyleOptions}
                        fontSizeOptions={fontSizeOptions}
                        insertFieldTypes={insertFieldTypes}
                        metadataFieldOptions={metadataFieldOptions}
                        productionFieldOptions={productionFieldOptions}
                        tagsTreeData={tagTreeData}
                        placeholder={t(
                          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.branding.custom.placeholder'
                        )}
                        disabled={isViewMode}
                      />
                    );
                  }
                )}

                {!isViewMode && (
                  <ArrButton
                    variant="secondary"
                    size="small"
                    onClick={handleAddCustomBranding}
                    className="typo-text buttonMedium_size add-custom-branding-btn"
                    startIcon={<AddIcon />}
                    dataTestId="branding-custom-addButton"
                  >
                    {t(
                      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.branding.custom.addButton'
                    )}
                  </ArrButton>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
      {/* Placeholders Section */}
      <Box
        className="placeholders-section"
        role="region"
        aria-labelledby="placeholders-section-title"
      >
        <Box className="main-section-header">
          <Typography
            id="placeholders-section-title"
            className="typo-text subhead3_size section-title"
            data-testid={`tiff-pdf-component-title-container`}
          >
            {t(
              'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.placeholders.title'
            )}
          </Typography>
          <ArrTooltipWrapper
            title={t(
              'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.tooltips.placeholders.title'
            )}
            trigger={<InfoIcon fontSize="medium" className="info-icon" />}
          >
            <Trans
              i18nKey="productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.tooltips.placeholders.content"
              components={{
                p: <p data-testid={`tiff-pdf-component-p-link`} />,
                strong: (
                  <strong data-testid={`tiff-pdf-component-strong-input`} />
                ),
                br: <br data-testid={`tiff-pdf-component-br-input`} />,
              }}
            />
          </ArrTooltipWrapper>
        </Box>

        <Box className="placeholders-content">
          {/* Automatically Placeholder and Export Natives for Docs with Imaging Errors */}
          <Box className="placeholder-option">
            {/* Description */}
            <Box className="placeholders-description">
              <Typography
                className="typo-text body3_size"
                data-testid={`tiff-pdf-component-typography`}
              >
                <Trans
                  i18nKey="productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.placeholders.description"
                  components={{
                    br: <br data-testid={`tiff-pdf-component-br-text`} />,
                  }}
                />
              </Typography>
            </Box>

            <Box className="toggle-option flex-end">
              <FormControlLabel
                control={
                  <ArrToggle
                    value={
                      placeholderSettings.shouldApplyPlaceholderForErroredDocs
                    }
                    onChange={(e) =>
                      handlePlaceholderToggle(
                        'shouldApplyPlaceholderForErroredDocs',
                        e.target.checked
                      )
                    }
                    toggleSize="medium"
                    disabled={isViewMode}
                    dataTestId="apply-placeholder-toggle"
                  />
                }
                label={
                  <Box>
                    <Typography
                      className="typo-text subhead4_size"
                      data-testid={`tiff-pdf-component-typography-text`}
                    >
                      <Trans
                        i18nKey="productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.placeholders.automaticallyPlaceholder.label"
                        components={{
                          br: (
                            <br
                              data-testid={`tiff-pdf-component-br-container`}
                            />
                          ),
                        }}
                      />
                    </Typography>
                  </Box>
                }
                labelPlacement="end"
              />
              <Box className="tooltip-bottom-margin">
                <ArrTooltipWrapper
                  title={t(
                    'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.tooltips.placeholderDocsForImagingErrors.title'
                  )}
                  trigger={
                    <InfoIcon fontSize="medium" className="info-icon-sm" />
                  }
                >
                  <Trans
                    i18nKey="productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.tooltips.placeholderDocsForImagingErrors.content"
                    components={{
                      p: <p data-testid={`tiff-pdf-component-p-input`} />,
                      strong: (
                        <strong
                          data-testid={`tiff-pdf-component-strong-text`}
                        />
                      ),
                      br: <br data-testid={`tiff-pdf-component-br-2`} />,
                    }}
                  />
                </ArrTooltipWrapper>
              </Box>
            </Box>
          </Box>

          <Divider className="section-divider" />

          {/* Enable for Privileged Docs */}
          <Box className="placeholder-option">
            <Box className="toggle-option">
              <FormControlLabel
                control={
                  <ArrToggle
                    value={placeholderSettings.enabledforPrivDocs}
                    onChange={(e) =>
                      handlePlaceholderToggle(
                        'enabledforPrivDocs',
                        e.target.checked
                      )
                    }
                    toggleSize="medium"
                    disabled={isViewMode}
                    dataTestId="privdocs-enabled-toggle"
                  />
                }
                label={
                  <Box>
                    <Typography
                      className="typo-text subhead4_size"
                      data-testid={`tiff-pdf-component-label-button`}
                    >
                      {t(
                        'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.placeholders.enableForPrivDocs.label'
                      )}
                    </Typography>
                  </Box>
                }
                labelPlacement="end"
              />
            </Box>
            {placeholderSettings.enabledforPrivDocs && (
              <Box className="placeholder-config">
                <Box className="config">
                  <Box className="config-row">
                    <Box className="config-field">
                      <TagsSection
                        selectedTags={placeholderSettings.selectedPrivTags}
                        onTagsChange={(tags) => {
                          if (isViewMode) return;
                          handlePlaceholderTagsChange('selectedPrivTags', tags);
                        }}
                        tagsTreeData={tagTreeData}
                        enabledForType="PrivilegedTag"
                        disabled={isViewMode}
                      />
                    </Box>
                    <Box className="config-field">
                      <TextPropertyToolbar
                        dataTestId="privilege-field"
                        variant="with-title"
                        title={t(
                          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.placeholders.placeholderText.label'
                        )}
                        toolbarText={placeholderSettings.phImageTextPrivDocs}
                        insertFieldTypes={insertFieldTypes}
                        metadataFieldOptions={metadataFieldOptions}
                        productionFieldOptions={productionFieldOptions}
                        onToolbarTextChange={(value) => {
                          if (isViewMode) return;
                          handlePlaceholderTextChange(
                            'phImageTextPrivDocs',
                            value
                          );
                        }}
                        onInsertField={(field) => {
                          if (isViewMode) return;
                          handlePlaceholderTextChange(
                            'phImageTextPrivDocs',
                            (placeholderSettings.phImageTextPrivDocs || '') +
                              `$${field}$`
                          );
                        }}
                        placeholder={t(
                          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.placeholders.placeholderText.placeholder.privDocs'
                        )}
                        disabled={isViewMode}
                        textAreaTestId={`privilege-docs-placeholder`}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>

          <Divider className="section-divider" />

          {/* Enable for Tech Issue Docs */}
          <Box className="placeholder-option">
            <Box className="toggle-option">
              <FormControlLabel
                control={
                  <ArrToggle
                    value={placeholderSettings.enabledforExceptionDocs}
                    onChange={(e) =>
                      handlePlaceholderToggle(
                        'enabledforExceptionDocs',
                        e.target.checked
                      )
                    }
                    toggleSize="medium"
                    disabled={isViewMode}
                    dataTestId="exceptional-docs-toggle"
                  />
                }
                label={
                  <Box>
                    <Typography
                      className="typo-text subhead4_size"
                      data-testid={`tiff-pdf-component-label-icon`}
                    >
                      {t(
                        'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.placeholders.enableForTechIssueDocs.label'
                      )}
                    </Typography>
                  </Box>
                }
                labelPlacement="end"
              />
            </Box>
            {placeholderSettings.enabledforExceptionDocs && (
              <Box className="placeholder-config">
                <Box className="config">
                  <Box className="config-row">
                    <Box className="config-field">
                      <TagsSection
                        selectedTags={placeholderSettings.selectedTechIssueTags}
                        onTagsChange={(tags) => {
                          if (isViewMode) return;
                          handlePlaceholderTagsChange(
                            'selectedTechIssueTags',
                            tags
                          );
                        }}
                        tagsTreeData={tagTreeData}
                        enabledForType="TechIssueTag"
                        disabled={isViewMode}
                      />
                    </Box>
                    <Box className="config-field">
                      <TextPropertyToolbar
                        variant="with-title"
                        dataTestId="tech-issue-tag"
                        title={t(
                          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.placeholders.placeholderText.label'
                        )}
                        toolbarText={
                          placeholderSettings.phImageTextTechIssueDocs
                        }
                        insertFieldTypes={insertFieldTypes}
                        metadataFieldOptions={metadataFieldOptions}
                        productionFieldOptions={productionFieldOptions}
                        onToolbarTextChange={(value) => {
                          if (isViewMode) return;
                          handlePlaceholderTextChange(
                            'phImageTextTechIssueDocs',
                            value
                          );
                        }}
                        onInsertField={(field) => {
                          if (isViewMode) return;
                          handlePlaceholderTextChange(
                            'phImageTextTechIssueDocs',
                            (placeholderSettings.phImageTextTechIssueDocs ||
                              '') + `$${field}$`
                          );
                        }}
                        placeholder={t(
                          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.placeholders.placeholderText.placeholder.techIssueDocs'
                        )}
                        disabled={isViewMode}
                        textAreaTestId={`techIssues-docs-placeholder`}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>

          <Divider className="section-divider" />

          {/* Enable for Natively Produced Docs */}
          <Box className="placeholder-option">
            <Box className="toggle-option">
              <FormControlLabel
                control={
                  <ArrToggle
                    value={placeholderSettings.enabledforNativeDocs}
                    onChange={(e) =>
                      handlePlaceholderToggle(
                        'enabledforNativeDocs',
                        e.target.checked
                      )
                    }
                    toggleSize="medium"
                    disabled={isViewMode}
                    dataTestId="native-docs-enabled-toggle"
                  />
                }
                label={
                  <Box>
                    <Typography
                      className="typo-text subhead4_size"
                      data-testid={`tiff-pdf-component-label-link`}
                    >
                      {t(
                        'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.placeholders.enableForNativeDocs.label'
                      )}
                    </Typography>
                  </Box>
                }
                labelPlacement="end"
              />
              <ArrTooltipWrapper
                title={t(
                  'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.tooltips.enableForNativeDocs.title'
                )}
                trigger={
                  <InfoIcon fontSize="medium" className="info-icon-sm" />
                }
              >
                <Trans
                  i18nKey="productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.tooltips.enableForNativeDocs.content"
                  components={{
                    p: <p data-testid={`tiff-pdf-component-p-container`} />,
                    strong: (
                      <strong
                        data-testid={`tiff-pdf-component-strong-container`}
                      />
                    ),
                    br: <br data-testid={`tiff-pdf-component-br-3`} />,
                  }}
                />
              </ArrTooltipWrapper>
            </Box>
            <Box className="natively-produced-description">
              <Typography
                className="typo-text body3_size"
                data-testid={`tiff-pdf-component-typography-btn`}
              >
                <Trans
                  i18nKey="productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.placeholders.enableForNativeDocs.description"
                  components={{
                    br: <br data-testid={`tiff-pdf-component-br-4`} />,
                  }}
                />
              </Typography>
            </Box>
            {placeholderSettings.enabledforNativeDocs && (
              <>
                {nativeImagePlaceholders.length > 0 && (
                  <Box className="native-docs-list">
                    {nativeImagePlaceholders.map((placeholder, index) => {
                      // Collect all tags from other items (excluding current item)
                      const disabledTags = nativeImagePlaceholders
                        .filter((item) => item.id !== placeholder.id)
                        .flatMap((item) => item.selectedPHTags || []);

                      return (
                        <CustomNativeDocsAccordion
                          key={placeholder.id}
                          id={placeholder.id}
                          index={index}
                          nativeDocsData={{
                            placeholderText: placeholder.placeholderText,
                            selectedTags: placeholder.selectedPHTags,
                            disabledTags: disabledTags,
                            brandingFont: DEFAULT_FONT_FAMILY,
                            brandingFontStyle: DEFAULT_FONT_STYLE,
                            brandingFontSize: parseInt(DEFAULT_FONT_SIZE),
                            fileTypes:
                              placeholder.selectedPHFileTypes &&
                              placeholder.selectedPHFileTypes.length > 0
                                ? placeholder.selectedPHFileTypes
                                : fileGroupTypeList || [],
                          }}
                          onDelete={handleRemoveImagePlaceholder}
                          onNativeDocsDataChange={(id, data) =>
                            handleUpdateImagePlaceholder(id, {
                              placeholderText: data.placeholderText || '',
                              selectedPHTags: data.selectedTags || [],
                              selectedPHFileTypes: data.fileTypes || [],
                            })
                          }
                          tagsTreeData={tagTreeData}
                          insertFieldTypes={insertFieldTypes}
                          metadataFieldOptions={metadataFieldOptions}
                          productionFieldOptions={productionFieldOptions}
                          placeholder={t(
                            'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.placeholders.placeholderText.placeholder.nativeDocs'
                          )}
                          disabled={isViewMode}
                        />
                      );
                    })}
                  </Box>
                )}
                {!isViewMode && (
                  <Box className="add-native-placeholder-btn-section">
                    <ArrButton
                      variant="secondary"
                      size="medium"
                      onClick={handleAddImagePlaceholder}
                      className="add-native-placeholder-btn typo-text buttonMedium_size"
                      startIcon={<AddIcon />}
                      dataTestId="branding-add-image-placeholder-button"
                    >
                      {t(
                        'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.placeholders.nativelyProducedDocs.addButton'
                      )}
                    </ArrButton>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
      {/* Redactions Section */}
      <Box
        className="redactions-section"
        role="region"
        aria-labelledby="redactions-section-title"
      >
        <Box className="main-section-header">
          <Typography
            id="redactions-section-title"
            className="typo-text subhead3_size section-title"
            data-testid={`tiff-pdf-component-title-2`}
          >
            {t(
              'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.title'
            )}
          </Typography>
          <ArrTooltipWrapper
            title={t(
              'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.tooltips.redactions.title'
            )}
            trigger={<InfoIcon fontSize="medium" className="info-icon" />}
          >
            <Trans
              i18nKey="productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.tooltips.redactions.content"
              components={{
                p: <p data-testid={`tiff-pdf-component-p-2`} />,
                strong: <strong data-testid={`tiff-pdf-component-strong-2`} />,
                br: <br data-testid={`tiff-pdf-component-br-5`} />,
              }}
            />
          </ArrTooltipWrapper>
        </Box>

        <Box className="redactions-content">
          {/* Burn Redactions Toggle */}
          <Box className="redaction-option">
            {/* Description */}
            <Box className="redactions-description">
              <Typography
                className="typo-text body3_size"
                data-testid={`tiff-pdf-component-typography-button`}
              >
                <Trans
                  i18nKey="productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.description"
                  components={{
                    br: <br data-testid={`tiff-pdf-component-br-6`} />,
                  }}
                />
              </Typography>
            </Box>
            <Box className="toggle-option">
              <FormControlLabel
                control={
                  <ArrToggle
                    value={redactionSettings.isBurnRedactionSelected}
                    onChange={(e) =>
                      handleRedactionToggle(
                        'isBurnRedactionSelected',
                        e.target.checked
                      )
                    }
                    toggleSize="medium"
                    disabled={isViewMode}
                    dataTestId="burn-redaction-toggle"
                  />
                }
                label={
                  <Box>
                    <Typography
                      className="typo-text subhead4_size"
                      data-testid={`tiff-pdf-component-label-input`}
                    >
                      {t(
                        'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.burnRedactions.label'
                      )}
                    </Typography>
                  </Box>
                }
                labelPlacement="end"
              />
            </Box>
          </Box>
          <Divider className="section-divider" />

          {/* Specify Redactions Section - Only show when Burn Redactions is enabled */}
          {redactionSettings.isBurnRedactionSelected && (
            <>
              <Box className="redaction-option specify-redactions-section">
                <Typography
                  className="typo-text subhead4_size section-subtitle"
                  data-testid={`tiff-pdf-component-title-3`}
                >
                  {t(
                    'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.specifyRedactions.title'
                  )}
                </Typography>

                <Box className="redaction-type-selection">
                  <Box className="redaction-type-radio-group">
                    <RadioGroup
                      row
                      value={redactionSettings.redactionType}
                      onChange={(e) => {
                        if (isViewMode) return;
                        setComponentIsDirty(true);
                        const selectedObj = redactionTypeOptions.find(
                          (opt) => opt.value?.toString() === e.target.value
                        );
                        if (selectedObj)
                          handleRedactionTypeChange(
                            Number(selectedObj.value) || 0
                          );
                      }}
                      className="custom-radio-group"
                      aria-label={t(
                        'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.specifyRedactions.title'
                      )}
                    >
                      {redactionTypeOptions.map(({value, label}, index) => {
                        return (
                          <FormControlLabel
                            key={value}
                            value={value}
                            control={
                              <Radio
                                icon={<RadioEnableIcon />}
                                checkedIcon={<RadioActiveIcon />}
                                className="typo-text body3_size"
                                inputProps={{
                                  'aria-label': String(label),
                                }}
                                disabled={isViewMode}
                              />
                            }
                            className="typo-text body3_size"
                            label={
                              <span
                                className="typo-text body3_size radio-text"
                                data-testid={`tiff-pdf-component-redaction-type-${index}`}
                              >
                                {label}
                              </span>
                            }
                            disabled={isViewMode}
                          />
                        );
                      })}
                    </RadioGroup>
                  </Box>

                  <Box className="redaction-style-dropdown">
                    <Typography
                      className="typo-text body3_size dropdown-title"
                      data-testid={`tiff-pdf-component-label-container`}
                    >
                      {t(
                        'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.redactionStyle.label'
                      )}
                    </Typography>
                    <ArrSingleSelectDropdown
                      dropdownLabel={t(
                        'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.redactionStyle.label'
                      )}
                      dropdownItems={redactionStyleOptions}
                      dropdownItemKey="value"
                      dropdownItemDisplay="label"
                      selectedValues={
                        redactionStyleOptions.find(
                          (item) =>
                            item.value === redactionSettings.redactionStyle
                        ) || redactionStyleOptions[0]
                      }
                      onSelectionChange={(item) => {
                        if (isViewMode) return;
                        setComponentIsDirty(true);
                        handleRedactionStyleChange(
                          String(item?.value) || DEFAULT_REDACTION_STYLE
                        );
                      }}
                      sizeVariant="medium"
                      dataTestId="asd-redaction-style-dropdown"
                      disabled={isViewMode}
                    />
                  </Box>
                </Box>

                {/* Redaction Tags Section - Only show when "Select a Redaction Tag" is selected */}
                {redactionSettings.redactionType === 0 && (
                  <Box className="redaction-tags-section">
                    <Box className="tags-config">
                      <TagsSection
                        headerTitle={t(
                          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.redactionTags.label'
                        )}
                        buttonTitle={t(
                          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.redactionTags.buttonText'
                        )}
                        selectedTags={redactionGlobalSelectedTags}
                        onTagsChange={(tags) => {
                          if (isViewMode) return;
                          setComponentIsDirty(true);
                          setRedactionGlobalSelectedTags(tags);
                        }}
                        tagsTreeData={redactionTagTreeData}
                        disabled={isViewMode}
                        moreItemSection={
                          <Box className="redaction-flyout-more-item">
                            <ArrCheckbox
                              data-testid="redaction-without-tags"
                              checked={isRedactionWithoutTags}
                              onChange={(checked) => {
                                if (isViewMode) return;
                                setComponentIsDirty(true);
                                setIsRedactionWithoutTags(checked);
                              }}
                              labelText={t(
                                'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.redactionWithoutTags.label'
                              )}
                              className="more-item-checkbox"
                              disabled={isViewMode}
                              aria-label={`redaction-without-tags-checkbox`}
                            />
                            <ArrTooltipWrapper
                              title={t(
                                'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.tooltips.redactionWithoutTags.title'
                              )}
                              trigger={
                                <InfoIcon
                                  fontSize="medium"
                                  className="info-icon"
                                />
                              }
                            >
                              <Trans
                                i18nKey="productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.tooltips.redactionWithoutTags.content"
                                components={{
                                  p: (
                                    <p data-testid={`tiff-pdf-component-p-3`} />
                                  ),
                                  br: (
                                    <br
                                      data-testid={`tiff-pdf-component-br-7`}
                                    />
                                  ),
                                }}
                              />
                            </ArrTooltipWrapper>
                          </Box>
                        }
                      />
                    </Box>
                  </Box>
                )}
              </Box>

              <Divider className="section-divider" />

              <Box className="redaction-option redaction-text-section">
                <Typography
                  className="typo-text subhead4_size section-subtitle"
                  data-testid={`tiff-pdf-component-label-2`}
                >
                  {t(
                    'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.redactionText.label'
                  )}
                </Typography>
                {/* Default Redaction Text */}
                <Box className="default-redaction-text">
                  <Typography
                    className="typo-text body3_size"
                    data-testid={`tiff-pdf-component-default-redaction-text`}
                  >
                    {t(
                      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.redactionText.defaultRedactionText'
                    )}
                  </Typography>
                  <Typography
                    className="typo-text body3_size"
                    data-testid={`tiff-pdf-component-default-redaction-abbreviation-text`}
                  >
                    {t(
                      'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.redactionText.defaultRedactionAbbreviationText'
                    )}
                  </Typography>
                </Box>

                {/* Redaction Data List using CustomRedactionTextAccordion */}
                {redactionSettings.redactionDataList.map((redaction, index) => {
                  // Collect all tags from other items (excluding current item)
                  const disabledTags = redactionSettings.redactionDataList
                    .filter((item) => item.id !== redaction.id)
                    .flatMap((item) => item.selectedTags || []);

                  return (
                    <CustomRedactionTextAccordion
                      key={redaction.id}
                      id={redaction.id}
                      index={index}
                      redactionTextData={{
                        selectedTags: redaction.selectedTags,
                        disabledTags: disabledTags,
                        placeholderText: redaction.redactionText,
                        abbreviatedText: redaction.abbrivatedText,
                      }}
                      onDelete={handleRemoveRedactionData}
                      onRedactionTextDataChange={(id, data) =>
                        handleUpdateRedactionData(id, {
                          selectedTags: data.selectedTags,
                          redactionText: data.placeholderText,
                          abbrivatedText: data.abbreviatedText,
                        })
                      }
                      tagsTreeData={redactionTagTreeData}
                      insertFieldTypes={insertFieldTypes}
                      metadataFieldOptions={metadataFieldOptions}
                      productionFieldOptions={productionFieldOptions}
                      disabled={isViewMode}
                    />
                  );
                })}

                {/* Add Redaction Data Button */}
                {!isViewMode && (
                  <Box className="add-redaction-data">
                    <ArrButton
                      variant="secondary"
                      size="medium"
                      onClick={handleAddRedactionData}
                      className="typo-text buttonMedium_size"
                      startIcon={<AddIcon />}
                      dataTestId="add-redaction-data-Button"
                    >
                      {t(
                        'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.redactionText.redactionItem.addButton'
                      )}
                      <ArrTooltipWrapper
                        title={t(
                          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.tooltips.redactionItemButton.title'
                        )}
                        trigger={
                          <InfoIcon
                            fontSize="medium"
                            className="info-icon-sm"
                          />
                        }
                      >
                        <Trans
                          i18nKey="productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.tooltips.redactionItemButton.content"
                          components={{
                            p: <p data-testid={`tiff-pdf-component-p-4`} />,
                            br: <br data-testid={`tiff-pdf-component-br-8`} />,
                          }}
                        />
                      </ArrTooltipWrapper>
                    </ArrButton>
                  </Box>
                )}
              </Box>

              <Divider className="section-divider" />
            </>
          )}

          {/* Generate Image Load File Toggle */}
          <Box className="redaction-option">
            <Box className="toggle-option">
              <FormControlLabel
                control={
                  <ArrToggle
                    value={generateImageLoadFile}
                    onChange={(e) => {
                      setComponentIsDirty(true);
                      handleGenerateImageLoadFileToggle(e.target.checked);
                    }}
                    toggleSize="medium"
                    disabled={isViewMode}
                    dataTestId="image-load-file-toggle"
                  />
                }
                label={
                  <Box>
                    <Typography
                      className="typo-text subhead4_size"
                      data-testid={`tiff-pdf-component-label-3`}
                    >
                      {t(
                        'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.generateImageLoadFile.label'
                      )}
                    </Typography>
                  </Box>
                }
                labelPlacement="end"
              />
            </Box>
            {/* Only show Load File Type dropdown when TIFF is selected (pageOption === 0) */}
            {pageOption === 0 && (
              <Box className="load-file-type-section">
                <Typography
                  className="typo-text body3_size field-label"
                  data-testid={`tiff-pdf-component-label-4`}
                >
                  {t(
                    'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.loadFileType.label'
                  )}
                </Typography>
                <ArrSingleSelectDropdown
                  dropdownLabel={
                    selectedLoadFileOption?.label || DEFAULT_LOAD_FILE_TYPE
                  }
                  dropdownItems={loadFileDropdownItems}
                  dropdownItemKey="value"
                  dropdownItemDisplay="label"
                  selectedValues={selectedLoadFileOption}
                  onSelectionChange={(item) => {
                    if (isViewMode) return;
                    setComponentIsDirty(true);
                    if (item?.value) setLoadFileType(String(item.value));
                  }}
                  sizeVariant="medium"
                  dataTestId="asd-load-file-type-dropdown"
                  disabled={isViewMode}
                />
              </Box>
            )}
          </Box>

          <Divider className="section-divider" />

          {/* Slip Sheets Toggle */}
          <Box className="redaction-option last-option">
            <Box className="toggle-option">
              <FormControlLabel
                control={
                  <ArrToggle
                    value={slipSheetSettings.isSlipSheetEnabled}
                    onChange={(e) => {
                      setComponentIsDirty(true);
                      handleSlipSheetToggle(e.target.checked);
                    }}
                    toggleSize="medium"
                    disabled={isViewMode}
                    dataTestId="slip-sheet-enabled-toggle"
                  />
                }
                label={
                  <Box>
                    <Typography
                      className="typo-text subhead4_size"
                      data-testid={`tiff-pdf-component-label-5`}
                    >
                      {t(
                        'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.redactions.slipSheets.label'
                      )}
                    </Typography>
                  </Box>
                }
                labelPlacement="end"
              />
              <ArrTooltipWrapper
                title={t(
                  'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.tooltips.slipSheets.title'
                )}
                trigger={
                  <InfoIcon fontSize="medium" className="info-icon-sm" />
                }
              >
                <Trans
                  i18nKey="productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.tooltips.slipSheets.content"
                  components={{
                    p: <p data-testid={`tiff-pdf-component-p-5`} />,
                    br: <br data-testid={`tiff-pdf-component-br-9`} />,
                  }}
                />
              </ArrTooltipWrapper>
            </Box>

            {slipSheetSettings.isSlipSheetEnabled && (
              <Box className="slip-sheets-section">
                <ArrTreeItemSelector
                  tabs={slipSheetTabs}
                  onSelectedItemsChange={handleSlipSheetItemsChange}
                  isPreExpanded={true}
                  disabled={isViewMode}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      {/* Blank Page Removal Modal */}
      <ArrModal
        showModal={showBlankPageModal}
        showClose={false}
        title={t(
          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.modals.blankPageRemoval.title'
        )}
        description={t(
          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.modals.blankPageRemoval.description'
        )}
        onClose={handleModalClose}
        dataTestId="tiff-pdf-modal"
        buttons={[
          {
            id: 'cancel',
            label: t(
              'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.modals.blankPageRemoval.cancel'
            ),
            variant: 'secondary',
            onClick: handleModalClose,
            dataTestId: 'tpm-cancel-btn',
          },
          {
            id: 'continue',
            label: t(
              'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.modals.blankPageRemoval.continue'
            ),
            variant: 'primary',
            onClick: handleModalContinue,
            dataTestId: 'tpm-continue-btn',
          },
        ]}
      />
      {/* Select Documents Modal */}
      <ArrModal
        showModal={showSelectDocumentsModal}
        headerTitle={t(
          'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.modals.selectDocuments.title'
        )}
        className="select-document-modal"
        headerTitleClassName="headline3_size document-header-title"
        onClose={handleSelectDocumentsModalClose}
        showClose={false}
        dataTestId="select-documents-modal"
        buttons={[
          {
            id: 'cancel',
            label: t(
              'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.modals.selectDocuments.cancel'
            ),
            variant: 'secondary',
            onClick: handleSelectDocumentsModalClose,
            dataTestId: 'sdm-cancel-btn',
          },
          {
            id: 'save',
            label: t(
              'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.modals.selectDocuments.save'
            ),
            variant: 'primary',
            onClick: handleSelectDocumentsTagsModalSave,
            dataTestId: 'sdm-save-btn',
            disabled:
              defaultBrandingSelectedTags.length <= 0 &&
              selectedDocuments.length <= 0,
          },
        ]}
      >
        <Box className="select-documents-content">
          {/* Description */}
          <Typography
            className="select-option-text typo-text subhead4_size"
            data-testid={`tiff-pdf-component-description`}
          >
            {t(
              'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.modals.selectDocuments.description'
            )}
          </Typography>

          {/* Selection Method Radio Buttons */}
          <Box className="selection-method-section">
            <RadioGroup
              row
              value={documentSelectionMethod}
              onChange={(e) => {
                if (isViewMode) return;
                setComponentIsDirty(true);
                const selectedObj = documentSelectionMethods.find(
                  (opt) => opt.value === e.target.value
                );
                if (selectedObj)
                  handleDocumentSelectionMethodChange(
                    String(selectedObj.value) ||
                      DocumentSelectionMethod.FILE_TYPE
                  );
              }}
              className="custom-radio-group"
              aria-label={t(
                'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.modals.selectDocuments.title'
              )}
            >
              {documentSelectionMethods.map(({value, label}, index) => {
                return (
                  <FormControlLabel
                    key={value}
                    value={value}
                    control={
                      <Radio
                        icon={<RadioEnableIcon />}
                        checkedIcon={<RadioActiveIcon />}
                        className="typo-text body3_size"
                        disabled={isViewMode}
                        slotProps={{
                          input: {
                            'aria-label': String(label),
                            'data-testid': `page-type-options-${index}`,
                          } as React.InputHTMLAttributes<HTMLInputElement>,
                        }}
                      />
                    }
                    className="typo-text body3_size"
                    label={
                      <span
                        className="typo-text body3_size radio-text"
                        data-testid={`tiff-pdf-component-doc-selection-method-${index}`}
                      >
                        {label}
                      </span>
                    }
                  />
                );
              })}
            </RadioGroup>
          </Box>

          {/* Document Selection - Conditional rendering based on method */}
          {documentSelectionMethod === DocumentSelectionMethod.FILE_TYPE && (
            <Box className="document-selection-section">
              <ArrMultiSelectDropdown
                dropdownLabel={t(
                  'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.modals.selectDocuments.selectDocumentsLabel'
                )}
                dropdownItems={fileTypeOptions}
                multiple
                selectedValues={selectedDocuments}
                onSelectionChange={(values) => {
                  if (isViewMode) return;
                  setSelectedDocuments(values);
                  setComponentIsDirty(true);
                }}
                sizeVariant="medium"
                searchable
                withControls
                customWidth={20}
                checkboxDataTestId="tiff-pdf-select-documents-dropdown-checkbox"
                data-testid="select-documents-multiselect"
                disabled={isViewMode}
              />
            </Box>
          )}

          {documentSelectionMethod === DocumentSelectionMethod.TAGS && (
            <Box className="document-selection-section tags">
              <TagsTreePanel
                type="normal"
                tags={tagsData}
                selectedTags={defaultBrandingSelectedTags.map(
                  convertTagItemToTreeView
                )}
                setSelectedTags={(
                  items: TreeViewBaseItem<ExtendedTreeItemProps>[]
                ) => {
                  const tagItems = items.map(convertTreeViewToTagItem);
                  setDefaultBrandingSelectedTags(tagItems);
                }}
                isPreExpanded={true}
              />
            </Box>
          )}
        </Box>
      </ArrModal>
    </Box>
  );
};

export default TiffPdfComponent;
